import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Box, Typography, Card, CardContent, Grid, Button,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Avatar, Chip, Paper, Stepper, Step, StepLabel, StepContent,
  Tabs, Tab, Fade, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,Tooltip,ClearIcon , Stack
} from '@mui/material';
import Timeline from '@mui/icons-material/Timeline';
import TrendingUp from '@mui/icons-material/TrendingUp';
import LocationOn from '@mui/icons-material/LocationOn';
import CalendarToday from '@mui/icons-material/CalendarToday';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Person from '@mui/icons-material/Person';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import Business from '@mui/icons-material/Business';
import BarChart from '@mui/icons-material/BarChart';
import Close from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../config/api';
import LOGO_FCN from './assets/image/LOGO_FCN.png';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import {
  BarChart as RechartsBar,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import html2canvas from 'html2canvas';

// Paleta de colores cálidos suaves - FCN
const theme = {
  primary: {
    main: '#8B6F47',      // Café suave
    light: '#A88B5F',     // Café claro
    dark: '#6B5536'       // Café oscuro
  },
  secondary: {
    main: '#D4A574',      // Beige dorado
    light: '#E8D4B8',     // Beige muy claro
    dark: '#C09560'       // Dorado oscuro
  },
  background: {
    default: '#FAF8F5',   // Fondo muy claro
    paper: '#FFFFFF',     // Blanco
    card: '#FFF9F0'       // Crema muy suave
  },
  text: {
    primary: '#4A3F35',   // Marrón oscuro para texto
    secondary: '#8B7D6B',  // Marrón gris
    muted: '#B8AFA3'      // Gris cálido
  },
  success: {
    main: '#7A9B6C',      // Verde oliva suave
    light: '#A5C09A',     // Verde claro
    dark: '#5F7D54'       // Verde oscuro
  },
  error: {
    main: '#C17B6F',      // Terracota suave
    light: '#D9A399',     // Rosa tierra
    dark: '#A05D52'       // Terracota oscuro
  },
  warning: {
    main: '#D9A86C',      // Dorado mostaza suave
    light: '#EBC999',     // Amarillo suave
    dark: '#C08F4F'       // Dorado oscuro
  },
  accent: {
    main: '#E8D5B7',      // Beige cálido
    light: '#F5EADB',     // Crema
    dark: '#D4BA8E'       // Dorado arena
  }
};

const COLORS = {
  vigente: theme.success.main,
  vencida: theme.warning.main,
  inhabilitado: theme.error.main,
  nuevo: theme.success.light,
  reelecto: theme.secondary.main
};
// Importar el logo

const meses = [
  { value: 0, label: 'Enero' },
  { value: 1, label: 'Febrero' },
  { value: 2, label: 'Marzo' },
  { value: 3, label: 'Abril' },
  { value: 4, label: 'Mayo' },
  { value: 5, label: 'Junio' },
  { value: 6, label: 'Julio' },
  { value: 7, label: 'Agosto' },
  { value: 8, label: 'Septiembre' },
  { value: 9, label: 'Octubre' },
  { value: 10, label: 'Noviembre' },
  { value: 11, label: 'Diciembre' }
];

const HistorialDepartamento = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [asociaciones, setAsociaciones] = useState([]);
  const [representantes, setRepresentantes] = useState([]);
  const [departamentoSeleccionado, setDepartamentoSeleccionado] = useState('');
  const [municipioSeleccionado, setMunicipioSeleccionado] = useState('');
  const [asociacionSeleccionada, setAsociacionSeleccionada] = useState('');
  const [mesSeleccionado, setMesSeleccionado] = useState('');
  const [anioSeleccionado, setAnioSeleccionado] = useState('');
  const [municipiosFiltrados, setMunicipiosFiltrados] = useState([]);
  const [historialData, setHistorialData] = useState(null);
  const [tabActiva, setTabActiva] = useState(0);
  const [deptosExpandido, setDeptosExpandido] = useState(false);
  const [municipiosExpandido, setMunicipiosExpandido] = useState(false);
  const [graficoSeleccionado, setGraficoSeleccionado] = useState('departamento');
  const [modalAbierto, setModalAbierto] = useState(false);
  const [representantesModal, setRepresentantesModal] = useState([]);
  const [tituloModal, setTituloModal] = useState('');

  // Referencias para capturar gráficos
  const graficoDeptoRef = useRef(null);
  const graficoMunicipioRef = useRef(null);
  const graficoPieRef = useRef(null);

  // Generar lista de años (2000 hasta el año actual)
  const generarAnios = () => {
    const anioActual = new Date().getFullYear();
    const anios = [];
    for (let i = anioActual; i >= 2000; i--) {
      anios.push(i);
    }
    return anios;
  };

  // Función para verificar si el representante pertenece a la categoría 3
  const tieneCategoria3 = (representante) => {
    if (!representante || !representante.categorias || !Array.isArray(representante.categorias)) {
      return false;
    }
    return representante.categorias.some(categoria =>
      categoria && (categoria.Id === 3 || categoria.CategoriaFK === 3) && categoria.Estado !== 'Inactivo'
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const datosReporte = await apiService.utils.getDatosReporte();
        // Filtrar solo los representantes de categoría 3
        const representantesCategoria3 = (datosReporte.representantes || []).filter(rep => tieneCategoria3(rep));
        setRepresentantes(representantesCategoria3);
        setDepartamentos(datosReporte.departamentos || []);
        setMunicipios(datosReporte.municipios || []);
        setAsociaciones(datosReporte.asociaciones || []);
      } catch (error) {
        console.error('Error al cargar datos:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);


  
  useEffect(() => {
    if (departamentoSeleccionado) {
      const filtered = municipios.filter(
        (mun) => String(mun.CodigoDepartamento) === String(departamentoSeleccionado)
      );
      setMunicipiosFiltrados(filtered);
      setMunicipioSeleccionado('');
    } else {
      setMunicipiosFiltrados([]);
    }
  }, [departamentoSeleccionado, municipios]);

 useEffect(() => {
  if (departamentoSeleccionado) {
    const filtered = municipios.filter(
      (mun) => String(mun.CodigoDepartamento) === String(departamentoSeleccionado)
    );
    setMunicipiosFiltrados(filtered);
    setMunicipioSeleccionado('');
  } else {
    setMunicipiosFiltrados([]);
  }
}, [departamentoSeleccionado, municipios]);

useEffect(() => {
  analizarHistorial();
}, [departamentoSeleccionado, municipioSeleccionado, asociacionSeleccionada, mesSeleccionado, anioSeleccionado, representantes]);

const analizarHistorial = () => {
  // PASO 1: Agrupar TODOS los representantes SOLO por DNI (historial global)
  // La clasificación nuevo/reelecto se basa en el historial completo de la persona
  const todosRepsPorDNI = {};
  representantes.forEach(rep => {
    if (!rep.dni) return;
    // Clave: solo DNI
    const key = rep.dni;
    if (!todosRepsPorDNI[key]) todosRepsPorDNI[key] = [];
    todosRepsPorDNI[key].push({
      ...rep,
      fecha_eleccion_date: rep.fecha_eleccion ? new Date(rep.fecha_eleccion) : null
    });
  });

  // Ordenar por fecha en cada grupo
  Object.values(todosRepsPorDNI).forEach(grupo => {
    grupo.sort((a, b) => (a.fecha_eleccion_date && b.fecha_eleccion_date)
      ? a.fecha_eleccion_date - b.fecha_eleccion_date
      : 0
    );
  });

  // Crear mapa de reelecciones basado SOLO en DNI
  // CRITERIO: Si un DNI aparece más de 1 vez (sin importar asociación), ES REELECTO
  const mapaReelecciones = new Map();
  Object.entries(todosRepsPorDNI).forEach(([dni, periodos]) => {
    const esReelecto = periodos.length > 1; // Si tiene más de 1 periodo globalmente, ES REELECTO

    periodos.forEach((periodo, index) => {
      const fechaISO = periodo.fecha_eleccion
        ? new Date(periodo.fecha_eleccion).toISOString().split('T')[0]
        : 'SIN_FECHA';
      // Usar DNI + Asociación + Fecha como clave única para identificar el registro
      const key = `${periodo.dni}_${periodo.cod_asociacion}_${fechaISO}`;
      mapaReelecciones.set(key, {
        esReeleccion: index > 0, // Solo el primer período del DNI es nuevo
        numeroPeriodo: index + 1,
        totalPeriodos: periodos.length
      });
    });
  });

  // PASO 2: Aplicar filtros sobre los datos originales
  let repsFiltered = [...representantes]; // Copia para no mutar

  if (departamentoSeleccionado) {
    repsFiltered = repsFiltered.filter(
      rep => String(rep.cod_depto) === String(departamentoSeleccionado)
    );
  }

  if (municipioSeleccionado) {
    repsFiltered = repsFiltered.filter(
      rep => String(rep.cod_municipio) === String(municipioSeleccionado)
    );
  }

  if (asociacionSeleccionada) {
    repsFiltered = repsFiltered.filter(
      rep => String(rep.cod_asociacion) === String(asociacionSeleccionada)
    );
  }

  if (mesSeleccionado !== '') {
    repsFiltered = repsFiltered.filter(
      rep => rep.fecha_eleccion && new Date(rep.fecha_eleccion).getMonth() === parseInt(mesSeleccionado)
    );
  }

  if (anioSeleccionado !== '') {
    repsFiltered = repsFiltered.filter(
      rep => rep.fecha_eleccion && new Date(rep.fecha_eleccion).getFullYear() === parseInt(anioSeleccionado)
    );
  }

  // PASO 3: Construir línea de tiempo con info de reelección correcta
  const lineaTiempo = repsFiltered.map(rep => {
    const fechaISO = rep.fecha_eleccion
      ? new Date(rep.fecha_eleccion).toISOString().split('T')[0]
      : 'SIN_FECHA';
    // Clave debe incluir DNI + Asociación + Fecha para buscar en el mapa
    const key = `${rep.dni}_${rep.cod_asociacion}_${fechaISO}`;
    const info = mapaReelecciones.get(key) || {
      esReeleccion: false,
      numeroPeriodo: 1,
      totalPeriodos: 1
    };

    return {
      ...rep,
      fecha_eleccion_date: rep.fecha_eleccion ? new Date(rep.fecha_eleccion) : null,
      esReeleccion: info.esReeleccion,
      numeroPeriodo: info.numeroPeriodo,
      totalPeriodos: info.totalPeriodos
    };
  });

  // Ordenar cronológicamente
  lineaTiempo.sort((a, b) =>
    (a.fecha_eleccion_date && b.fecha_eleccion_date)
      ? a.fecha_eleccion_date - b.fecha_eleccion_date
      : 0
  );

  // PASO 4: Calcular estadísticas (usando datos filtrados + mapa global)
  const vigentes = repsFiltered.filter(r => r.estatus === 'VIGENTE').length;
  const vencidos = repsFiltered.filter(r => r.estatus === 'VENCIDA').length;
  const inhabilitados = repsFiltered.filter(r => r.estatus === 'INHABILITADO').length;


  // Agrupar por DNI solo los vigentes y vencidos
  const dnisVigentesVencidos = {};
  const registrosSinDNI = [];

  lineaTiempo.forEach(r => {
    if (r.estatus === 'VIGENTE' || r.estatus === 'VENCIDA') {
      if (r.dni) {
        if (!dnisVigentesVencidos[r.dni]) {
          dnisVigentesVencidos[r.dni] = [];
        }
        dnisVigentesVencidos[r.dni].push(r);
      } else {
        // Registros sin DNI se guardan aparte
        registrosSinDNI.push(r);
      }
    }
  });

  // Contar PERSONAS ÚNICAS (cada DNI cuenta solo una vez)
  let nuevos = 0;
  let reelectos = 0;

  Object.keys(dnisVigentesVencidos).forEach(dni => {
    // Verificar en el historial completo (todosRepsPorDNI) si esta persona tiene más de 1 periodo
    const todosLosPeriodos = todosRepsPorDNI[dni] || [];

    if (todosLosPeriodos.length > 1) {
      // Es reelecto si tiene más de 1 periodo en el historial completo (incluyendo inhabilitados)
      reelectos++;
    } else {
      // Es nuevo si solo tiene 1 periodo
      nuevos++;
    }
  });

  // Los registros SIN DNI se cuentan como NUEVOS (no se puede verificar reelección)
  nuevos += registrosSinDNI.length;

  const dnisFiltrados = new Set(Object.keys(dnisVigentesVencidos));

  // Total de REPRESENTANTES REGISTRADOS: personas únicas (nuevos + reelectos)
  const totalRegistros = nuevos + reelectos;

  // Promedio de reelecciones (basado en DNI)
  const personasConReeleccion = Object.values(todosRepsPorDNI).filter(g => g.length > 1).length;
  const totalReelecciones = Object.values(todosRepsPorDNI).reduce((sum, g) => sum + Math.max(0, g.length - 1), 0);
  const promedioReelecciones = personasConReeleccion > 0
    ? (totalReelecciones / personasConReeleccion).toFixed(2)
    : '0.00';

  const stats = {
    totalRegistros,
    vigentes,
    vencidos,
    inhabilitados,
    nuevos,
    reelectos,
    promedioReelecciones,
    totalPersonasUnicas: dnisFiltrados.size  // DNIs únicos (personas reales)
  };

  // PASO 5: Preparar gráficos (usar agrupamiento por DNI y mapa de reelecciones)
  const graficos = prepararDatosGraficos(repsFiltered, todosRepsPorDNI, lineaTiempo, mapaReelecciones);

  // Cobertura
  const deptosUnicos = [...new Set(repsFiltered.map(r => r.cod_depto))];
  const munsUnicos = [...new Set(repsFiltered.map(r => r.cod_municipio))];

  setHistorialData({
    lineaTiempo,
    estadisticas: stats,
    representantesPorDNI: todosRepsPorDNI, // agrupamiento por DNI
    filtroAsociacionActiva: asociacionSeleccionada,
    graficos,
    cobertura: {
      departamentos: deptosUnicos.length,
      municipios: munsUnicos.length,
      departamentosList: deptosUnicos,
      municipiosList: munsUnicos
    }
  });
};






 const calcularEstadisticas = (reps, porDNI) => {
  const vigentes = reps.filter(r => r.estatus === 'VIGENTE').length;
  const vencidos = reps.filter(r => r.estatus === 'VENCIDA').length;
  const inhabilitados = reps.filter(r => r.estatus === 'INHABILITADO').length;

  // Filtrar solo vigentes y vencidos para el conteo de nuevos/reelectos
  const repsVigentesVencidos = reps.filter(r => r.estatus === 'VIGENTE' || r.estatus === 'VENCIDA');

  // Agrupar por DNI solo los vigentes/vencidos
  const porDNIVigentesVencidos = {};
  repsVigentesVencidos.forEach(rep => {
    if (!rep.dni) return;
    if (!porDNIVigentesVencidos[rep.dni]) {
      porDNIVigentesVencidos[rep.dni] = [];
    }
    porDNIVigentesVencidos[rep.dni].push(rep);
  });

  // Total de REGISTROS: solo vigentes + vencidos (excluye inhabilitados)
  const totalRegistros = vigentes + vencidos;

  // Validación: debe cumplirse
  if (reps.length !== vigentes + vencidos + inhabilitados) {
    console.warn('Inconsistencia en conteo:', { total: reps.length, vigentes, vencidos, inhabilitados });
  }

  // Calcular nuevos y reelectos solo sobre vigentes/vencidos
  // pero usando el porDNI completo para determinar si es reelección
  const totalPersonasUnicasVigentesVencidos = Object.keys(porDNIVigentesVencidos).length;

  // Una persona es reelecta si tiene más de un periodo en vigente/vencido
  // O si tiene periodos en vigente/vencido y también tuvo periodos inhabilitados
  let reelectos = 0;
  Object.keys(porDNIVigentesVencidos).forEach(dni => {
    const periodosVigentesVencidos = porDNIVigentesVencidos[dni].length;
    const todosPeriodos = porDNI[dni] ? porDNI[dni].length : periodosVigentesVencidos;

    // Es reelecto si tiene más de 1 periodo en total (incluyendo inhabilitados anteriores)
    if (todosPeriodos > 1) {
      reelectos++;
    }
  });

  const nuevos = totalPersonasUnicasVigentesVencidos - reelectos;

  const promedioReelecciones = reelectos > 0
    ? (Object.values(porDNI).reduce((sum, grupo) => sum + (grupo.length - 1), 0) / reelectos).toFixed(2)
    : 0;

  return {
    totalRegistros,           // Solo vigentes + vencidos
    vigentes,
    vencidos,
    inhabilitados,
    totalPersonasUnicas: totalPersonasUnicasVigentesVencidos,  // Solo personas con registros vigentes/vencidos
    nuevos,                   // Solo nuevos en vigentes/vencidos
    reelectos,                // Solo reelectos en vigentes/vencidos (pero considerando historial completo)
    promedioReelecciones,
  };
};
  const prepararDatosGraficos = (reps, porDNI, lineaTiempo, mapaReelecciones) => {
    const porEstado = [
      { name: 'Vigentes', value: reps.filter(r => r.estatus === 'VIGENTE').length, color: COLORS.vigente },
      { name: 'Vencidos', value: reps.filter(r => r.estatus === 'VENCIDA').length, color: COLORS.vencida },
      { name: 'Inhabilitados', value: reps.filter(r => r.estatus === 'INHABILITADO').length, color: COLORS.inhabilitado }
    ].filter(item => item.value > 0);

    // Contar REGISTROS usando el mapa de reelecciones (solo el primer período es nuevo)
    let nuevosCount = 0;
    let reelectosCount = 0;
    lineaTiempo.forEach(rep => {
      if (!rep.dni) return;
      const fechaISO = rep.fecha_eleccion
        ? new Date(rep.fecha_eleccion).toISOString().split('T')[0]
        : 'SIN_FECHA';
      const key = `${rep.dni}_${rep.cod_asociacion}_${fechaISO}`;
      const info = mapaReelecciones.get(key);

      if (info && info.esReeleccion) {
        reelectosCount++;
      } else {
        nuevosCount++;
      }
    });

    const porTipo = [
      { name: 'Nuevos', value: nuevosCount, color: COLORS.nuevo },
      { name: 'Reelectos', value: reelectosCount, color: COLORS.reelecto }
    ].filter(item => item.value > 0);

    // Por Departamento: contar REGISTROS, clasificar usando mapa de reelecciones
    const porDepartamento = {};
    lineaTiempo.forEach(rep => {
      if (!rep.dni) return;
      const nombreDepto = getNombreDepartamento(rep.cod_depto);
      if (!porDepartamento[nombreDepto]) {
        porDepartamento[nombreDepto] = {
          departamento: nombreDepto,
          registros: [],
          nuevosCount: 0,
          reelectosCount: 0
        };
      }

      // Cada registro se cuenta individualmente
      porDepartamento[nombreDepto].registros.push(rep);

      // Clasificar usando el mapa de reelecciones
      const fechaISO = rep.fecha_eleccion
        ? new Date(rep.fecha_eleccion).toISOString().split('T')[0]
        : 'SIN_FECHA';
      const key = `${rep.dni}_${rep.cod_asociacion}_${fechaISO}`;
      const info = mapaReelecciones.get(key);

      if (info && info.esReeleccion) {
        porDepartamento[nombreDepto].reelectosCount++;
      } else {
        porDepartamento[nombreDepto].nuevosCount++;
      }
    });

    const datosDepartamentos = Object.entries(porDepartamento).map(([nombre, data]) => ({
      departamento: nombre,
      cantidad: data.registros.length,
      nuevos: data.nuevosCount,
      reelectos: data.reelectosCount
    })).sort((a, b) => b.cantidad - a.cantidad);

    // Por Municipio: contar REGISTROS, clasificar usando mapa de reelecciones (filtrado por departamento seleccionado)
    const porMunicipio = {};
    lineaTiempo.forEach(rep => {
      if (!rep.dni) return;
      // Si hay departamento seleccionado, filtrar solo municipios de ese departamento
      if (departamentoSeleccionado && String(rep.cod_depto) !== String(departamentoSeleccionado)) {
        return;
      }

      const nombreMun = getNombreMunicipio(rep.cod_municipio);
      const codMun = rep.cod_municipio;
      const codDepto = rep.cod_depto;

      if (!porMunicipio[codMun]) {
        porMunicipio[codMun] = {
          municipio: nombreMun,
          cod_municipio: codMun,
          cod_depto: codDepto,
          registros: [],
          nuevosCount: 0,
          reelectosCount: 0
        };
      }

      // Cada registro se cuenta individualmente
      porMunicipio[codMun].registros.push(rep);

      // Clasificar usando el mapa de reelecciones
      const fechaISO = rep.fecha_eleccion
        ? new Date(rep.fecha_eleccion).toISOString().split('T')[0]
        : 'SIN_FECHA';
      const key = `${rep.dni}_${rep.cod_asociacion}_${fechaISO}`;
      const info = mapaReelecciones.get(key);

      if (info && info.esReeleccion) {
        porMunicipio[codMun].reelectosCount++;
      } else {
        porMunicipio[codMun].nuevosCount++;
      }
    });

    const datosMunicipios = Object.entries(porMunicipio).map(([cod, data]) => ({
      municipio: data.municipio,
      cod_municipio: data.cod_municipio,
      cod_depto: data.cod_depto,
      cantidad: data.registros.length,
      nuevos: data.nuevosCount,
      reelectos: data.reelectosCount
    })).sort((a, b) => b.cantidad - a.cantidad);

    // Por Año: contar REGISTROS, clasificar usando mapa de reelecciones
    const porAnio = {};
    lineaTiempo.forEach(rep => {
      if (!rep.dni || !rep.fecha_eleccion) return;
      const anio = new Date(rep.fecha_eleccion).getFullYear();
      if (!porAnio[anio]) {
        porAnio[anio] = {
          anio: anio.toString(),
          registros: [],
          nuevosCount: 0,
          reelectosCount: 0
        };
      }

      // Cada registro se cuenta individualmente
      porAnio[anio].registros.push(rep);

      // Clasificar usando el mapa de reelecciones
      const fechaISO = rep.fecha_eleccion
        ? new Date(rep.fecha_eleccion).toISOString().split('T')[0]
        : 'SIN_FECHA';
      const key = `${rep.dni}_${rep.cod_asociacion}_${fechaISO}`;
      const info = mapaReelecciones.get(key);

      if (info && info.esReeleccion) {
        porAnio[anio].reelectosCount++;
      } else {
        porAnio[anio].nuevosCount++;
      }
    });

    const datosEvolucion = Object.entries(porAnio).map(([anio, data]) => ({
      anio,
      total: data.registros.length,
      nuevos: data.nuevosCount,
      reelectos: data.reelectosCount
    })).sort((a, b) => parseInt(a.anio) - parseInt(b.anio));

    const datosAsociaciones = [];
    if (!asociacionSeleccionada) {
      const porAsociacion = {};
      reps.forEach(rep => {
        const nombreAsoc = getNombreAsociacion(rep.cod_asociacion);
        if (!porAsociacion[nombreAsoc]) {
          porAsociacion[nombreAsoc] = 0;
        }
        porAsociacion[nombreAsoc]++;
      });

      datosAsociaciones.push(...Object.entries(porAsociacion)
        .map(([nombre, cantidad]) => ({ asociacion: nombre, cantidad }))
        .sort((a, b) => b.cantidad - a.cantidad)
        .slice(0, 10));
    }

    return {
      porEstado,
      porTipo,
      datosDepartamentos,
      datosMunicipios,
      datosEvolucion,
      datosAsociaciones
    };
  };

  const getNombreDepartamento = (cod) => {
    const depto = departamentos.find((d) => d.CodigoDepartamento === cod);
    return depto ? depto.NombreDepartamento : 'N/A';
  };

  const getNombreMunicipio = (cod) => {
    const mun = municipios.find((m) => m.CodigoMunicipio === cod);
    return mun ? mun.NombreMunicipio : 'N/A';
  };

  const getNombreAsociacion = (cod) => {
    const asoc = asociaciones.find((a) => a.cod_asociacion === cod);
    return asoc ? asoc.nombre_abreviado : 'N/A';
  };

  const formatDate = (date, formatType = 'display') => {
    if (!date || date === '') {
      return formatType === 'legible' ? 'N/A' : '';
    }

    let d;

    if (typeof date === 'string') {
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = date.split('-').map(num => parseInt(num, 10));
        d = new Date(year, month - 1, day);
      } else {
        d = new Date(date);
      }
    } else {
      d = new Date(date);
    }

    if (isNaN(d.getTime())) {
      return formatType === 'legible' ? 'N/A' : '';
    }

    if (formatType === 'filename') {
      return `${d.getFullYear()}${(d.getMonth() + 1).toString().padStart(2, '0')}${d.getDate().toString().padStart(2, '0')}`;
    }

    if (formatType === 'legible') {
      return d.toLocaleDateString('es-HN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    return d.toISOString().split('T')[0];
  };

  const getStatusColor = (estatus) => {
    switch (estatus) {
      case 'VIGENTE': return theme.success.main;
      case 'VENCIDA': return theme.warning.main;
      case 'INHABILITADO': return theme.error.main;
      default: return theme.text.secondary;
    }
  };

  const limpiarFiltros = () => {
    setDepartamentoSeleccionado('');
    setMunicipioSeleccionado('');
    setAsociacionSeleccionada('');
    setMesSeleccionado('');
    setAnioSeleccionado('');
    setMunicipiosFiltrados([]);
    setHistorialData(null);
    setTabActiva(0);
    setDeptosExpandido(false);
  };

  const handleClickBarra = (data, tipo, origen = 'departamento') => {
    if (!data || !historialData) return;

    // Usar el agrupamiento por DNI (historial global)
    const agrupamientoAUsar = historialData.representantesPorDNI;
    const filtroAsociacion = historialData.filtroAsociacionActiva;

    // Primero, obtener los DNIs únicos que cumplen con el filtro
    let dnisUnicos = new Set();

    if (origen === 'departamento') {
      const departamento = data.departamento;

      // Obtener DNIs únicos de este departamento, clasificados por historial global
      historialData.lineaTiempo.forEach(rep => {
        const nombreDepto = getNombreDepartamento(rep.cod_depto);
        if (nombreDepto === departamento) {
          const totalPeriodos = agrupamientoAUsar[rep.dni]?.length || 1;
          const esReelecto = totalPeriodos > 1;

          if ((tipo === 'nuevos' && !esReelecto) || (tipo === 'reelectos' && esReelecto)) {
            dnisUnicos.add(rep.dni);
          }
        }
      });

      setTituloModal(`${tipo === 'nuevos' ? 'Nuevos' : 'Reelectos'} Representantes - ${departamento}`);
    } else if (origen === 'municipio') {
      const municipio = data.municipio;
      const codMunicipio = data.cod_municipio;

      // Obtener DNIs únicos de este municipio, clasificados por historial global
      historialData.lineaTiempo.forEach(rep => {
        if (String(rep.cod_municipio) === String(codMunicipio)) {
          const totalPeriodos = agrupamientoAUsar[rep.dni]?.length || 1;
          const esReelecto = totalPeriodos > 1;

          if ((tipo === 'nuevos' && !esReelecto) || (tipo === 'reelectos' && esReelecto)) {
            dnisUnicos.add(rep.dni);
          }
        }
      });

      const nombreDepto = getNombreDepartamento(data.cod_depto);
      setTituloModal(`${tipo === 'nuevos' ? 'Nuevos' : 'Reelectos'} Representantes - ${municipio} (${nombreDepto})`);
    }

    // Ahora, para cada DNI único, obtener TODOS sus periodos
    const repsAgrupados = [];

    dnisUnicos.forEach(dni => {
      // Obtener todos los periodos de este DNI
      const todosLosPeriodos = agrupamientoAUsar[dni] || [];

      // Filtrar solo los periodos que están en el lugar específico (departamento o municipio)
      // Y también por asociación si hay filtro activo
      let periodosFiltrados = [];

      if (origen === 'departamento') {
        const departamento = data.departamento;
        periodosFiltrados = todosLosPeriodos.filter(periodo => {
          const nombreDepto = getNombreDepartamento(periodo.cod_depto);
          const matchDepto = nombreDepto === departamento;

          // Si hay filtro de asociación, también validar
          if (filtroAsociacion) {
            return matchDepto && String(periodo.cod_asociacion) === String(filtroAsociacion);
          }
          return matchDepto;
        });
      } else if (origen === 'municipio') {
        const codMunicipio = data.cod_municipio;
        periodosFiltrados = todosLosPeriodos.filter(periodo => {
          const matchMunicipio = String(periodo.cod_municipio) === String(codMunicipio);

          // Si hay filtro de asociación, también validar
          if (filtroAsociacion) {
            return matchMunicipio && String(periodo.cod_asociacion) === String(filtroAsociacion);
          }
          return matchMunicipio;
        });
      }

      if (periodosFiltrados.length > 0) {
        // Ordenar periodos de más antiguo a más reciente
        periodosFiltrados.sort((a, b) => {
          const fechaA = a.fecha_eleccion ? new Date(a.fecha_eleccion) : null;
          const fechaB = b.fecha_eleccion ? new Date(b.fecha_eleccion) : null;
          if (!fechaA || !fechaB) return 0;
          return fechaA - fechaB;
        });

        // Contar el total de periodos únicos
        const totalPeriodos = periodosFiltrados.length;

        // Determinar si es reelección: basado en historial global del DNI
        const totalPeriodosGlobales = agrupamientoAUsar[dni]?.length || 0;
        const esReeleccion = totalPeriodosGlobales > 1;

        repsAgrupados.push({
          ...periodosFiltrados[0],
          periodos: periodosFiltrados,
          esReeleccion: esReeleccion,
          totalPeriodosEnContexto: totalPeriodos
        });
      }
    });

    setRepresentantesModal(repsAgrupados);
    setModalAbierto(true);
  };

  const getTituloAnalisis = () => {
    let titulo = '';
    if (departamentoSeleccionado && !asociacionSeleccionada) {
      titulo = `Análisis del Departamento de ${getNombreDepartamento(departamentoSeleccionado)}`;
    } else if (departamentoSeleccionado && asociacionSeleccionada) {
      titulo = `Análisis de ${getNombreAsociacion(asociacionSeleccionada)} en ${getNombreDepartamento(departamentoSeleccionado)}`;
    
    }
    const periodoTexto = (mesSeleccionado !== '' && anioSeleccionado !== '')
      ? ` - ${meses.find(m => m.value === parseInt(mesSeleccionado))?.label} ${anioSeleccionado}`
      : '';
    return titulo + periodoTexto;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1.5, backgroundColor: 'white', border: `2px solid ${theme.primary.main}` }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: theme.primary.dark }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="caption" sx={{ color: entry.color, display: 'block' }}>
              {entry.name}: {entry.value}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

 const exportarPDF = () => {
  if (!historialData) return;

  const doc = new jsPDF({ orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let y = 20;

  // Función helper para añadir encabezado en cada página
  const addHeader = (incluirLogo = true) => {
    let headerY = 15;
    if (incluirLogo) {
      const logoWidth = 35;
      const logoHeight = 35;
      const logoX = (pageWidth - logoWidth) / 2;
      doc.addImage(LOGO_FCN, 'PNG', logoX, headerY, logoWidth, logoHeight);
      headerY += logoHeight + 5;
    }
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(105, 86, 74);
   
    return headerY + 8;
  };

  // === PORTADA ===
  // Centrar verticalmente el contenido de la portada
  let portadaY = pageHeight / 2 - 60;

  const logoWidth = 50;
  const logoHeight = 50;
  const logoX = (pageWidth - logoWidth) / 2;
  doc.addImage(LOGO_FCN, 'PNG', logoX, portadaY, logoWidth, logoHeight);
  portadaY += logoHeight + 15;

  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(105, 86, 74);
  doc.text('FONDO CAFETERO NACIONAL', pageWidth / 2, portadaY, { align: 'center' });
  portadaY += 15;

  doc.setFontSize(18);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text('Historial de Representantes', pageWidth / 2, portadaY, { align: 'center' });
  portadaY += 12;

  doc.setFontSize(14);
  doc.text(getTituloAnalisis(), pageWidth / 2, portadaY, { align: 'center' });
  portadaY += 20;

  // Filtros aplicados
  doc.setFontSize(11);
  doc.setTextColor(100, 100, 100);
  const filtros = [];
  if (departamentoSeleccionado) filtros.push(`Departamento: ${getNombreDepartamento(departamentoSeleccionado)}`);
  if (municipioSeleccionado) filtros.push(`Municipio: ${getNombreMunicipio(municipioSeleccionado)}`);
  if (asociacionSeleccionada) filtros.push(`Asociación: ${getNombreAsociacion(asociacionSeleccionada)}`);
  if (mesSeleccionado !== '' && anioSeleccionado !== '') {
    filtros.push(`Período: ${meses.find(m => m.value === parseInt(mesSeleccionado))?.label} ${anioSeleccionado}`);
  }

  if (filtros.length > 0) {
    filtros.forEach((filtro) => {
      doc.text(filtro, pageWidth / 2, portadaY, { align: 'center' });
      portadaY += 8;
    });
  }

  portadaY += 10;
  doc.setFontSize(10);
  doc.text(`Generado: ${formatDate(new Date())}`, pageWidth / 2, portadaY, { align: 'center' });

  // Nueva página para el contenido
  doc.addPage();
  y = 20;

  // Agrupar por asociación para usar en sección de detalle
  const porAsociacion = {};
  historialData.lineaTiempo.forEach(rep => {
    const asoc = getNombreAsociacion(rep.cod_asociacion);
    if (!porAsociacion[asoc]) {
      porAsociacion[asoc] = {
        total: 0,
        vigentes: 0,
        vencidos: 0,
        inhabilitados: 0,
        nuevos: 0,
        reelectos: 0,
        departamentos: new Set(),
        municipios: new Set()
      };
    }
    porAsociacion[asoc].total++;
    if (rep.estatus === 'VIGENTE') porAsociacion[asoc].vigentes++;
    if (rep.estatus === 'VENCIDA') porAsociacion[asoc].vencidos++;
    if (rep.estatus === 'INHABILITADO') porAsociacion[asoc].inhabilitados++;
    if (rep.esReeleccion) porAsociacion[asoc].reelectos++;
    else porAsociacion[asoc].nuevos++;
    porAsociacion[asoc].departamentos.add(getNombreDepartamento(rep.cod_depto));
    porAsociacion[asoc].municipios.add(getNombreMunicipio(rep.cod_municipio));
  });

  // === ANÁLISIS COMPARATIVO NUEVOS VS REELECTOS (si hay filtro de año) ===
  if (anioSeleccionado !== '') {
    doc.addPage();
    y = addHeader();

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(105, 86, 74);
    doc.text(`REPRESENTANTES ELECTOS EN EL AÑO ${anioSeleccionado}`, margin, y);
    y += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text('Análisis basado en fecha de elección', margin, y);
    y += 10;

    // Agrupar por departamento/municipio
    const ingresosPorDpto = {};
    historialData.lineaTiempo.forEach(rep => {
      const depto = getNombreDepartamento(rep.cod_depto);
      const muni = getNombreMunicipio(rep.cod_municipio);

      if (!ingresosPorDpto[depto]) {
        ingresosPorDpto[depto] = {
          nuevos: [],
          reelectos: [],
          municipios: {}
        };
      }

      if (!ingresosPorDpto[depto].municipios[muni]) {
        ingresosPorDpto[depto].municipios[muni] = {
          nuevos: [],
          reelectos: []
        };
      }

      if (rep.esReeleccion) {
        ingresosPorDpto[depto].reelectos.push(rep);
        ingresosPorDpto[depto].municipios[muni].reelectos.push(rep);
      } else {
        ingresosPorDpto[depto].nuevos.push(rep);
        ingresosPorDpto[depto].municipios[muni].nuevos.push(rep);
      }
    });

    // Resumen general del año
    const totalNuevosAnio = historialData.lineaTiempo.filter(r => !r.esReeleccion).length;
    const totalReelectosAnio = historialData.lineaTiempo.filter(r => r.esReeleccion).length;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 80, 80);
    const textoFiltro = asociacionSeleccionada
      ? `Asociación: ${getNombreAsociacion(asociacionSeleccionada)}`
      : 'Todas las asociaciones';
    doc.text(textoFiltro, margin, y);
    y += 8;

    // Tabla resumen
    doc.autoTable({
      startY: y,
      head: [['TIPO DE REPRESENTANTE', 'CANTIDAD', 'PORCENTAJE']],
      body: [
        ['Nuevos Electos', totalNuevosAnio, `${((totalNuevosAnio / (totalNuevosAnio + totalReelectosAnio)) * 100).toFixed(1)}%`],
        ['Reelectos', totalReelectosAnio, `${((totalReelectosAnio / (totalNuevosAnio + totalReelectosAnio)) * 100).toFixed(1)}%`],
        ['TOTAL', totalNuevosAnio + totalReelectosAnio, '100%']
      ],
      theme: 'grid',
      headStyles: { fillColor: [105, 86, 74], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      styles: { fontSize: 9, cellPadding: 4 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 80 },
        1: { halign: 'center', fontStyle: 'bold', cellWidth: 40 },
        2: { halign: 'center', cellWidth: 40 }
      }
    });
    y = doc.lastAutoTable.finalY + 12;

    // Detalle por departamento y municipio
   
  }

  

  // Agrupar representantes por asociación
  const repsPorAsociacion = {};
  historialData.lineaTiempo.forEach(rep => {
    const asoc = getNombreAsociacion(rep.cod_asociacion);
    if (!repsPorAsociacion[asoc]) repsPorAsociacion[asoc] = [];
    repsPorAsociacion[asoc].push(rep);
  });

  // Ordenar asociaciones alfabéticamente
  Object.entries(repsPorAsociacion)
    .sort((a, b) => a[0].localeCompare(b[0]))
    .forEach(([asoc, reps]) => {
      // Verificar espacio para nueva asociación
      if (y > 160) {
        doc.addPage();
        y = addHeader();
      }

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(105, 86, 74);
      doc.text(`ASOCIACIÓN: ${asoc}`, margin, y);
      y += 6;

      // Calcular estadísticas de la asociación
      const stats = porAsociacion[asoc];
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(
        `Total: ${stats.total} | Vigentes: ${stats.vigentes} | Vencidos: ${stats.vencidos} | Inhabilitados: ${stats.inhabilitados} | Nuevos: ${stats.nuevos} | Reelectos: ${stats.reelectos}`,
        margin,
        y
      );
      y += 8;

      // Ordenar representantes por fecha de elección
      const repsOrdenados = [...reps].sort((a, b) => {
        const fechaA = a.fecha_eleccion_date || new Date(0);
        const fechaB = b.fecha_eleccion_date || new Date(0);
        return fechaB - fechaA; // Más reciente primero
      });

      const detalleData = repsOrdenados.map((rep, i) => [
        i + 1,
        rep.dni || 'N/A',
        rep.nombre_representante || 'N/A',
        getNombreDepartamento(rep.cod_depto),
        getNombreMunicipio(rep.cod_municipio),
        formatDate(rep.fecha_eleccion),
        formatDate(rep.fecha_vencimiento),
        rep.estatus || 'N/A',
        rep.esReeleccion ? 'Reelecto' : 'Nuevo',
        '' // Columna de firma vacía
      ]);

      doc.autoTable({
        startY: y,
        head: [['#', 'DNI', 'NOMBRE', 'DEPTO.', 'MUNI.', 'F. ELECCIÓN', 'F. VENC.', 'ESTATUS', 'TIPO', 'FIRMA']],
        body: detalleData,
        theme: 'striped',
        headStyles: { fillColor: [166, 124, 82], textColor: 255, fontStyle: 'bold', fontSize: 6 },
        styles: { fontSize: 5.5, cellPadding: 1.5 },
        columnStyles: {
          0: { cellWidth: 6, halign: 'center' },
          1: { cellWidth: 16 },
          2: { cellWidth: 34 },
          3: { cellWidth: 20 },
          4: { cellWidth: 24 },
          5: { cellWidth: 18 },
          6: { cellWidth: 18 },
          7: { cellWidth: 16, halign: 'center' },
          8: { cellWidth: 14, halign: 'center' },
          9: { cellWidth: 17, halign: 'center' }
        },
        didParseCell: function(data) {
          // Colorear según estatus
          if (data.column.index === 7 && data.section === 'body') {
            const estatus = data.cell.raw;
            if (estatus === 'VIGENTE') {
              data.cell.styles.textColor = [123, 91, 62];
              data.cell.styles.fontStyle = 'bold';
            } else if (estatus === 'VENCIDA') {
              data.cell.styles.textColor = [225, 156, 0];
              data.cell.styles.fontStyle = 'bold';
            } else if (estatus === 'INHABILITADO') {
              data.cell.styles.textColor = [200, 168, 140];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      });
      y = doc.lastAutoTable.finalY + 10;
    });

 
  

  // === FOOTER EN TODAS LAS PÁGINAS ===
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `© ${new Date().getFullYear()} FONDO CAFETERO NACIONAL`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
    doc.text(`Página ${i} de ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  // === GUARDAR ===
  const periodoTexto = mesSeleccionado !== '' && anioSeleccionado !== ''
    ? `${meses.find(m => m.value === parseInt(mesSeleccionado))?.label}_${anioSeleccionado}`
    : 'Completo';
  doc.save(`FCN_Historial_Representantes_${periodoTexto}_${new Date().toISOString().slice(0, 10)}.pdf`);
};

  const exportarExcel = () => {
    if (!historialData) return;

    const wb = XLSX.utils.book_new();
    const currentDate = new Date();

    const repsPeriodo = historialData.lineaTiempo;
    const periodoTexto = mesSeleccionado !== '' && anioSeleccionado !== ''
      ? `${meses.find(m => m.value === parseInt(mesSeleccionado))?.label} ${anioSeleccionado}`
      : 'Todo el período';

    // Función auxiliar para ajustar anchos de columna
    const autoAjustarColumnas = (data) => {
      const colWidths = [];
      data.forEach(row => {
        row.forEach((cell, colIndex) => {
          const cellValue = cell ? String(cell) : '';
          const cellLength = cellValue.length;
          if (!colWidths[colIndex] || colWidths[colIndex] < cellLength) {
            colWidths[colIndex] = Math.min(cellLength + 2, 50); // Max 50 caracteres
          }
        });
      });
      return colWidths.map(w => ({ wch: Math.max(w, 10) })); // Mínimo 10 caracteres
    };

    // Hoja 1: Resumen Ejecutivo
    const resumenHeaderRow = [`HISTORIAL REPRESENTANTES - RESUMEN EJECUTIVO`];
    const resumenSubHeaderRow = [`Reporte generado: ${formatDate(currentDate)} | Período: ${periodoTexto}`];
    const emptyRow = ['', ''];

    const resumenData = [
      resumenHeaderRow,
      resumenSubHeaderRow,
      emptyRow,
      ['ESTADÍSTICAS GENERALES', ''],
      [`Total Representantes en el Período (${periodoTexto})`, repsPeriodo.length],
      ['Total Personas Únicas (DNI)', historialData.estadisticas.totalPersonasUnicas],
      emptyRow,
      ['DISTRIBUCIÓN POR TIPO', ''],
      ['Representantes Nuevos', historialData.estadisticas.nuevos],
      ['Representantes Reelectos', historialData.estadisticas.reelectos],
      ['Promedio de Reelecciones', historialData.estadisticas.promedioReelecciones],
      emptyRow,
      ['DISTRIBUCIÓN POR ESTATUS', ''],
      ['Vigentes', historialData.estadisticas.vigentes],
      ['Vencidos', historialData.estadisticas.vencidos],
      ['Inhabilitados', historialData.estadisticas.inhabilitados],
      emptyRow,
      ['COBERTURA GEOGRÁFICA', ''],
      ['Departamentos cubiertos', historialData.cobertura.departamentos],
      ['Municipios cubiertos', historialData.cobertura.municipios],
      emptyRow,
      ['FILTROS APLICADOS', ''],
      departamentoSeleccionado ? ['Departamento', getNombreDepartamento(departamentoSeleccionado)] : ['Departamento', 'Todos'],
      municipioSeleccionado ? ['Municipio', getNombreMunicipio(municipioSeleccionado)] : ['Municipio', 'Todos'],
      asociacionSeleccionada ? ['Asociación', getNombreAsociacion(asociacionSeleccionada)] : ['Asociación', 'Todas'],
    ];

    const wsResumen = XLSX.utils.aoa_to_sheet(resumenData);
    wsResumen['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }, // Título principal
      { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } }, // Subtítulo
      { s: { r: 3, c: 0 }, e: { r: 3, c: 1 } }, // ESTADÍSTICAS GENERALES
      { s: { r: 8, c: 0 }, e: { r: 8, c: 1 } }, // DISTRIBUCIÓN POR TIPO
      { s: { r: 12, c: 0 }, e: { r: 12, c: 1 } }, // DISTRIBUCIÓN POR ESTATUS
      { s: { r: 17, c: 0 }, e: { r: 17, c: 1 } }, // COBERTURA GEOGRÁFICA
      { s: { r: 21, c: 0 }, e: { r: 21, c: 1 } }  // FILTROS APLICADOS
    ];
    wsResumen['!cols'] = autoAjustarColumnas(resumenData);
    XLSX.utils.book_append_sheet(wb, wsResumen, 'Resumen Ejecutivo');

    // Hoja 2: Representantes del Período Seleccionado
    const periodoHeaders = ['DNI', 'Nombre', 'Departamento', 'Municipio', 'Asociación', 'Fecha de Elección', 'Estatus', 'Tipo'];
    const periodoHeaderRow = [`REPRESENTANTES DEL PERÍODO - ${periodoTexto}`];
    const periodoSubHeaderRow = [`Total registros: ${repsPeriodo.length}`];

    const periodoData = [
      periodoHeaderRow,
      periodoSubHeaderRow,
      emptyRow,
      periodoHeaders,
      ...repsPeriodo.map(rep => [
        rep.dni || 'N/A',
        rep.nombre_representante || 'N/A',
        getNombreDepartamento(rep.cod_depto),
        getNombreMunicipio(rep.cod_municipio),
        getNombreAsociacion(rep.cod_asociacion),
        formatDate(rep.fecha_eleccion),
        rep.estatus || 'N/A',
        rep.esReeleccion ? 'Reelecto' : 'Nuevo'
      ])
    ];

    const wsPeriodo = XLSX.utils.aoa_to_sheet(periodoData);
    wsPeriodo['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: periodoHeaders.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: periodoHeaders.length - 1 } }
    ];
    wsPeriodo['!cols'] = autoAjustarColumnas(periodoData);
    XLSX.utils.book_append_sheet(wb, wsPeriodo, 'Representantes del Período');

    // Hoja 3: Representantes Nuevos
    const nuevosHeaders = ['DNI', 'Nombre', 'Departamento', 'Municipio', 'Asociación', 'Fecha de Elección', 'Estatus'];
    const nuevosHeaderRow = [`REPRESENTANTES NUEVOS - ${periodoTexto}`];
    const representantesNuevos = historialData.lineaTiempo.filter(rep => !rep.esReeleccion);
    const nuevosSubHeaderRow = [`Total registros: ${representantesNuevos.length}`];

    const nuevosData = [
      nuevosHeaderRow,
      nuevosSubHeaderRow,
      emptyRow,
      nuevosHeaders,
      ...representantesNuevos.map(rep => [
        rep.dni || 'N/A',
        rep.nombre_representante || 'N/A',
        getNombreDepartamento(rep.cod_depto),
        getNombreMunicipio(rep.cod_municipio),
        getNombreAsociacion(rep.cod_asociacion),
        formatDate(rep.fecha_eleccion),
        rep.estatus || 'N/A'
      ])
    ];

    const wsNuevos = XLSX.utils.aoa_to_sheet(nuevosData);
    wsNuevos['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: nuevosHeaders.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: nuevosHeaders.length - 1 } }
    ];
    wsNuevos['!cols'] = autoAjustarColumnas(nuevosData);
    XLSX.utils.book_append_sheet(wb, wsNuevos, 'Representantes Nuevos');

    // Hoja 5: Representantes Reelectos
    const reelectosHeaders = ['DNI', 'Nombre', 'Departamento', 'Municipio', 'Asociación', 'Fecha de Elección', 'Estatus', 'Número de Periodo'];
    const reelectosHeaderRow = [`REPRESENTANTES REELECTOS - ${periodoTexto}`];
    const representantesReelectos = historialData.lineaTiempo.filter(rep => rep.esReeleccion);
    const reelectosSubHeaderRow = [`Total registros: ${representantesReelectos.length}`];

    const reelectosData = [
      reelectosHeaderRow,
      reelectosSubHeaderRow,
      emptyRow,
      reelectosHeaders,
      ...representantesReelectos.map(rep => [
        rep.dni || 'N/A',
        rep.nombre_representante || 'N/A',
        getNombreDepartamento(rep.cod_depto),
        getNombreMunicipio(rep.cod_municipio),
        getNombreAsociacion(rep.cod_asociacion),
        formatDate(rep.fecha_eleccion),
        rep.estatus || 'N/A',
        rep.numeroPeriodo
      ])
    ];

    const wsReelectos = XLSX.utils.aoa_to_sheet(reelectosData);
    wsReelectos['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: reelectosHeaders.length - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: reelectosHeaders.length - 1 } }
    ];
    wsReelectos['!cols'] = autoAjustarColumnas(reelectosData);
    XLSX.utils.book_append_sheet(wb, wsReelectos, 'Representantes Reelectos');

    // Guardar el archivo Excel
    const excelFile = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelFile], { type: 'application/octet-stream' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Informe_Historial_${periodoTexto.replace(/ /g, '_')}_${formatDate(currentDate, 'filename')}.xlsx`;
    link.click();
  };

  // Función auxiliar para capturar gráfico como imagen
  const capturarGraficoComoImagen = async (refElement) => {
    if (!refElement || !refElement.current) return null;

    try {
      const canvas = await html2canvas(refElement.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false
      });
      return canvas.toDataURL('image/png').split(',')[1]; // Retornar solo el base64
    } catch (error) {
      console.error('Error capturando gráfico:', error);
      return null;
    }
  };

  // Nueva función para exportar Excel con gráficos usando ExcelJS
  const exportarExcelConGraficos = async () => {
    if (!historialData) return;

    // Capturar gráficos como imágenes antes de crear el Excel
    const imagenDeptos = await capturarGraficoComoImagen(graficoDeptoRef);
    const imagenPie = await capturarGraficoComoImagen(graficoPieRef);

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Federación Cafetera Nacional';
    workbook.created = new Date();

    const currentDate = new Date();
    const periodoTexto = mesSeleccionado !== '' && anioSeleccionado !== ''
      ? `${meses.find(m => m.value === parseInt(mesSeleccionado))?.label} ${anioSeleccionado}`
      : 'Todo el período';

    // === HOJA 1: RESUMEN EJECUTIVO ===
    const wsResumen = workbook.addWorksheet('Resumen Ejecutivo');
    wsResumen.columns = [
      { header: '', key: 'concepto', width: 40 },
      { header: '', key: 'valor', width: 20 }
    ];

    // Título principal
    wsResumen.mergeCells('A1:B1');
    wsResumen.getCell('A1').value = 'HISTORIAL REPRESENTANTES ';
    wsResumen.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
    wsResumen.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF8B6F47' }
    };
    wsResumen.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };
    wsResumen.getRow(1).height = 25;

    // Subtítulo
    wsResumen.mergeCells('A2:B2');
    wsResumen.getCell('A2').value = `Reporte generado: ${formatDate(currentDate)} | Período: ${periodoTexto}`;
    wsResumen.getCell('A2').font = { size: 10, italic: true };
    wsResumen.getCell('A2').alignment = { horizontal: 'center' };

    let row = 4;

    // ESTADÍSTICAS GENERALES
    wsResumen.mergeCells(`A${row}:B${row}`);
    wsResumen.getCell(`A${row}`).value = 'ESTADÍSTICAS GENERALES';
    wsResumen.getCell(`A${row}`).font = { bold: true, size: 12, color: { argb: 'FF8B6F47' } };
    row++;

    // DISTRIBUCIÓN POR TIPO
    wsResumen.mergeCells(`A${row}:B${row}`);
    wsResumen.getCell(`A${row}`).value = 'DISTRIBUCIÓN POR TIPO';
    wsResumen.getCell(`A${row}`).font = { bold: true, size: 12, color: { argb: 'FF8B6F47' } };
    row++;

    [
      ['Representantes Nuevos', historialData.estadisticas.nuevos],
      ['Representantes Reelectos', historialData.estadisticas.reelectos],
      
    ].forEach(([concepto, valor]) => {
      wsResumen.getCell(`A${row}`).value = concepto;
      wsResumen.getCell(`B${row}`).value = valor;
      row++;
    });

    row++;

    // DISTRIBUCIÓN POR ESTATUS
    wsResumen.mergeCells(`A${row}:B${row}`);
    wsResumen.getCell(`A${row}`).value = 'DISTRIBUCIÓN POR ESTATUS';
    wsResumen.getCell(`A${row}`).font = { bold: true, size: 12, color: { argb: 'FF8B6F47' } };
    row++;

    [
      ['Vigentes', historialData.estadisticas.vigentes],
      ['Vencidos', historialData.estadisticas.vencidos],
 
    ].forEach(([concepto, valor]) => {
      wsResumen.getCell(`A${row}`).value = concepto;
      wsResumen.getCell(`B${row}`).value = valor;
      wsResumen.getCell(`B${row}`).numFmt = '#,##0';
      row++;
    });

    // === HOJA 2: GRÁFICOS ===
    const wsGraficos = workbook.addWorksheet('Gráficos y Visualizaciones');
    wsGraficos.columns = [
      { header: '', key: 'col1', width: 30 },
      { header: '', key: 'col2', width: 15 },
      { header: '', key: 'col3', width: 15 },
      { header: '', key: 'col4', width: 15 }
    ];

    // Título
    wsGraficos.mergeCells('A1:D1');
    wsGraficos.getCell('A1').value = 'ANÁLISIS GRÁFICO Y ESTADÍSTICO';
    wsGraficos.getCell('A1').font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
    wsGraficos.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF8B6F47' }
    };
    wsGraficos.getCell('A1').alignment = { horizontal: 'center', vertical: 'middle' };

    // Datos para gráfico de barras - Top 10 Departamentos
    row = 3;
    wsGraficos.mergeCells(`A${row}:D${row}`);
    wsGraficos.getCell(`A${row}`).value = 'TOP 10 DEPARTAMENTOS';
    wsGraficos.getCell(`A${row}`).font = { bold: true, size: 12 };
    row++;

    wsGraficos.getCell(`A${row}`).value = 'Departamento';
    wsGraficos.getCell(`B${row}`).value = 'Total';
    wsGraficos.getCell(`C${row}`).value = 'Nuevos';
    wsGraficos.getCell(`D${row}`).value = 'Reelectos';
    wsGraficos.getRow(row).font = { bold: true };
    wsGraficos.getRow(row).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFD3C5B0' }
    };
    row++;

    const topDepartamentos = historialData.graficos.datosDepartamentos.slice(0, 10);
    const startRow = row;
    topDepartamentos.forEach(depto => {
      wsGraficos.getCell(`A${row}`).value = depto.departamento;
      wsGraficos.getCell(`B${row}`).value = depto.cantidad;
      wsGraficos.getCell(`C${row}`).value = depto.nuevos;
      wsGraficos.getCell(`D${row}`).value = depto.reelectos;

      // Aplicar formato condicional con colores
      wsGraficos.getCell(`B${row}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE8DCC8' }
      };
      wsGraficos.getCell(`C${row}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFC8E6C9' }
      };
      wsGraficos.getCell(`D${row}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFCDD2' }
      };

      row++;
    });

    // Agregar imagen del gráfico de departamentos si está disponible
    row += 2;
    if (imagenDeptos) {
      const imageIdDeptos = workbook.addImage({
        base64: imagenDeptos,
        extension: 'png'
      });

      wsGraficos.addImage(imageIdDeptos, {
        tl: { col: 0, row: row },
        ext: { width: 600, height: 400 }
      });

      row += 22; // Ajustar espacio para la imagen
    } else {
      wsGraficos.mergeCells(`A${row}:D${row}`);
      wsGraficos.getCell(`A${row}`).value = 'NOTA: No se pudo capturar el gráfico. Seleccione los datos y use Insertar > Gráfico en Excel';
      wsGraficos.getCell(`A${row}`).font = { italic: true, size: 10 };
      wsGraficos.getCell(`A${row}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFE082' }
      };
      row++;
    }

    // Agregar distribución por tipo
    row += 3;
    wsGraficos.mergeCells(`A${row}:D${row}`);
    wsGraficos.getCell(`A${row}`).value = 'DISTRIBUCIÓN POR TIPO DE REPRESENTANTE';
    wsGraficos.getCell(`A${row}`).font = { bold: true, size: 12 };
    row++;

    wsGraficos.getCell(`A${row}`).value = 'Tipo';
    wsGraficos.getCell(`B${row}`).value = 'Cantidad';
    wsGraficos.getCell(`C${row}`).value = 'Porcentaje';
    wsGraficos.getRow(row).font = { bold: true };
    wsGraficos.getRow(row).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFD3C5B0' }
    };
    row++;

    const totalReps = historialData.estadisticas.nuevos + historialData.estadisticas.reelectos;
    const distribucionTipo = [
      ['Nuevos', historialData.estadisticas.nuevos, `${((historialData.estadisticas.nuevos / totalReps) * 100).toFixed(1)}%`],
      ['Reelectos', historialData.estadisticas.reelectos, `${((historialData.estadisticas.reelectos / totalReps) * 100).toFixed(1)}%`]
    ];

    distribucionTipo.forEach(([tipo, cantidad, porcentaje]) => {
      wsGraficos.getCell(`A${row}`).value = tipo;
      wsGraficos.getCell(`B${row}`).value = cantidad;
      wsGraficos.getCell(`C${row}`).value = porcentaje;

      wsGraficos.getCell(`B${row}`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: tipo === 'Nuevos' ? 'FFC8E6C9' : 'FFFFE082' }
      };

      row++;
    });

    // Agregar imagen del gráfico de pie si está disponible
    row += 2;
    if (imagenPie) {
      const imageIdPie = workbook.addImage({
        base64: imagenPie,
        extension: 'png'
      });

      wsGraficos.addImage(imageIdPie, {
        tl: { col: 0, row: row },
        ext: { width: 400, height: 300 }
      });

      row += 18; // Ajustar espacio para la imagen
    }

    // === HOJA 3: REPRESENTANTES DEL PERÍODO ===
    const wsPeriodo = workbook.addWorksheet('Representantes del Período');
    wsPeriodo.columns = [
      { header: 'DNI', key: 'dni', width: 15 },
      { header: 'Nombre', key: 'nombre', width: 35 },
      { header: 'Departamento', key: 'departamento', width: 20 },
      { header: 'Municipio', key: 'municipio', width: 25 },
      { header: 'Asociación', key: 'asociacion', width: 20 },
      { header: 'Fecha Elección', key: 'fecha', width: 15 },
      { header: 'Estatus', key: 'estatus', width: 12 },
      { header: 'Tipo', key: 'tipo', width: 12 }
    ];

    // Estilo del encabezado
    wsPeriodo.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    wsPeriodo.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF8B6F47' }
    };
    wsPeriodo.getRow(1).alignment = { horizontal: 'center', vertical: 'middle' };

    // Agregar datos
    historialData.lineaTiempo.forEach(rep => {
      wsPeriodo.addRow({
        dni: rep.dni || 'N/A',
        nombre: rep.nombre_representante || 'N/A',
        departamento: getNombreDepartamento(rep.cod_depto),
        municipio: getNombreMunicipio(rep.cod_municipio),
        asociacion: getNombreAsociacion(rep.cod_asociacion),
        fecha: formatDate(rep.fecha_eleccion),
        estatus: rep.estatus || 'N/A',
        tipo: rep.esReeleccion ? 'Reelecto' : 'Nuevo'
      });
    });

    // === HOJA 4: NUEVOS ===
    const wsNuevos = workbook.addWorksheet('Representantes Nuevos');
    wsNuevos.columns = wsPeriodo.columns.filter(col => col.key !== 'tipo');
    wsNuevos.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    wsNuevos.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF4CAF50' }
    };

    historialData.lineaTiempo.filter(rep => !rep.esReeleccion).forEach(rep => {
      wsNuevos.addRow({
        dni: rep.dni || 'N/A',
        nombre: rep.nombre_representante || 'N/A',
        departamento: getNombreDepartamento(rep.cod_depto),
        municipio: getNombreMunicipio(rep.cod_municipio),
        asociacion: getNombreAsociacion(rep.cod_asociacion),
        fecha: formatDate(rep.fecha_eleccion),
        estatus: rep.estatus || 'N/A'
      });
    });

    // === HOJA 5: REELECTOS ===
    const wsReelectos = workbook.addWorksheet('Representantes Reelectos');
    wsReelectos.columns = [
      ...wsPeriodo.columns.filter(col => col.key !== 'tipo'),
      { header: 'Nº Periodo', key: 'periodo', width: 12 }
    ];
    wsReelectos.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    wsReelectos.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFF9800' }
    };

    historialData.lineaTiempo.filter(rep => rep.esReeleccion).forEach(rep => {
      wsReelectos.addRow({
        dni: rep.dni || 'N/A',
        nombre: rep.nombre_representante || 'N/A',
        departamento: getNombreDepartamento(rep.cod_depto),
        municipio: getNombreMunicipio(rep.cod_municipio),
        asociacion: getNombreAsociacion(rep.cod_asociacion),
        fecha: formatDate(rep.fecha_eleccion),
        estatus: rep.estatus || 'N/A',
        periodo: rep.numeroPeriodo
      });
    });

    // Guardar el archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Informe_Historial_${periodoTexto.replace(/ /g, '_')}_${formatDate(currentDate, 'filename')}.xlsx`;
    link.click();
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress size={60} sx={{ color: theme.primary.main }} />
      </Container>
    );
  }

  const hayFiltroActivo = departamentoSeleccionado || municipioSeleccionado || asociacionSeleccionada || mesSeleccionado !== '' || anioSeleccionado !== '';

  return (
    <Box sx={{ backgroundColor: theme.background.default, height: '100vh', overflow: 'auto', overflowX: 'hidden' }}>
      <Container
        maxWidth="xl"
        sx={{
          py: 0.5,
          px: { xs: 1, sm: 1.5, md: 2 },
          height: '100%',
          overflowX: 'visible'
        }}
      >
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            backgroundColor: theme.primary.main,
            borderRadius: 2,
            border: `1px solid ${theme.primary.dark}`
          }}
        >
          <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  backgroundColor: theme.primary.light,
                  borderRadius: 2,
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Timeline sx={{ fontSize: 32, color: 'white' }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: '1.5rem',
                    lineHeight: 1.2
                  }}
                >
                  Historial de Representantes
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.accent.light,
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    display: 'block',
                    mt: 0.5
                  }}
                >
                  Seguimiento de representantes municipales gremiales 
                </Typography>
              </Box>
            </Box>

            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/reporteria')}
              variant="contained"
              sx={{
                backgroundColor: 'white',
                color: theme.primary.main,
                fontWeight: 600,
                px: 3,
                py: 1,
                fontSize: '0.9rem',
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: theme.accent.light,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }
              }}
            >
              Volver
            </Button>
          </Box>
        </Paper>

        {/* Panel de Filtros Moderno */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            backgroundColor: 'white',
            borderRadius: 3,
            border: `1px solid ${theme.secondary.light}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.accent.main,
              px: 1.5,
              py: 0.75,
              borderBottom: `1px solid ${theme.accent.dark}`
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: theme.primary.dark,
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75
              }}
            >
              <LocationOn sx={{ fontSize: 18 }} />
              Filtros de Búsqueda
            </Typography>
          </Box>

          <CardContent sx={{ p: 1.5 }}>
            <Grid container spacing={1.5} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: theme.background.card,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid transparent',
                      '&:hover': {
                        backgroundColor: 'white',
                        borderColor: theme.primary.light,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        borderColor: theme.primary.main,
                        boxShadow: `0 0 0 2px ${theme.primary.light}20`
                      }
                    }
                  }}
                >
                  <InputLabel sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Departamento</InputLabel>
                  <Select
                    value={departamentoSeleccionado}
                    onChange={(e) => setDepartamentoSeleccionado(e.target.value)}
                    label="Departamento"
                    sx={{ fontSize: '0.85rem' }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          mt: 0.5,
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          maxHeight: 240,
                          '& .MuiMenuItem-root': {
                            fontSize: '0.85rem',
                            py: 0.75,
                            px: 2,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              backgroundColor: theme.primary.light + '15',
                              paddingLeft: 2.5
                            },
                            '&.Mui-selected': {
                              backgroundColor: theme.primary.light + '25',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: theme.primary.light + '35'
                              }
                            }
                          }
                        }
                      },
                      transitionDuration: 200,
                      TransitionProps: {
                        timeout: 200
                      }
                    }}
                  >
                    <MenuItem value=""><em>Todos los departamentos</em></MenuItem>
                    {departamentos.map((d) => (
                      <MenuItem key={d.CodigoDepartamento} value={d.CodigoDepartamento}>
                        {d.NombreDepartamento}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl
                  fullWidth
                  size="small"
                  disabled={!departamentoSeleccionado}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: theme.background.card,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid transparent',
                      '&:hover': {
                        backgroundColor: 'white',
                        borderColor: theme.primary.light,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        borderColor: theme.primary.main,
                        boxShadow: `0 0 0 2px ${theme.primary.light}20`
                      }
                    }
                  }}
                >
                  <InputLabel sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Municipio</InputLabel>
                  <Select
                    value={municipioSeleccionado}
                    onChange={(e) => setMunicipioSeleccionado(e.target.value)}
                    label="Municipio"
                    sx={{ fontSize: '0.85rem' }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          mt: 0.5,
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          maxHeight: 240,
                          '& .MuiMenuItem-root': {
                            fontSize: '0.85rem',
                            py: 0.75,
                            px: 2,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              backgroundColor: theme.primary.light + '15',
                              paddingLeft: 2.5
                            },
                            '&.Mui-selected': {
                              backgroundColor: theme.primary.light + '25',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: theme.primary.light + '35'
                              }
                            }
                          }
                        }
                      },
                      transitionDuration: 200,
                      TransitionProps: {
                        timeout: 200
                      }
                    }}
                  >
                    <MenuItem value=""><em>Todos los municipios</em></MenuItem>
                    {municipiosFiltrados.map((m) => (
                      <MenuItem key={m.CodigoMunicipio} value={m.CodigoMunicipio}>
                        {m.NombreMunicipio}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: theme.background.card,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid transparent',
                      '&:hover': {
                        backgroundColor: 'white',
                        borderColor: theme.primary.light,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        borderColor: theme.primary.main,
                        boxShadow: `0 0 0 2px ${theme.primary.light}20`
                      }
                    }
                  }}
                >
                  <InputLabel sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Asociación</InputLabel>
                  <Select
                    value={asociacionSeleccionada}
                    onChange={(e) => setAsociacionSeleccionada(e.target.value)}
                    label="Asociación"
                    sx={{ fontSize: '0.85rem' }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          mt: 0.5,
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          maxHeight: 240,
                          '& .MuiMenuItem-root': {
                            fontSize: '0.85rem',
                            py: 0.75,
                            px: 2,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              backgroundColor: theme.primary.light + '15',
                              paddingLeft: 2.5
                            },
                            '&.Mui-selected': {
                              backgroundColor: theme.primary.light + '25',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: theme.primary.light + '35'
                              }
                            }
                          }
                        }
                      },
                      transitionDuration: 200,
                      TransitionProps: {
                        timeout: 200
                      }
                    }}
                  >
                    <MenuItem value=""><em>Todas las asociaciones</em></MenuItem>
                    {asociaciones.map((a) => (
                      <MenuItem key={a.cod_asociacion} value={a.cod_asociacion}>
                        {a.nombre_abreviado}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6} sm={3} md={1.5}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: theme.background.card,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid transparent',
                      '&:hover': {
                        backgroundColor: 'white',
                        borderColor: theme.primary.light,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        borderColor: theme.primary.main,
                        boxShadow: `0 0 0 2px ${theme.primary.light}20`
                      }
                    }
                  }}
                >
                  <InputLabel sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Mes</InputLabel>
                  <Select
                    value={mesSeleccionado}
                    onChange={(e) => setMesSeleccionado(e.target.value)}
                    label="Mes"
                    sx={{ fontSize: '0.85rem' }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          mt: 0.5,
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          maxHeight: 240,
                          '& .MuiMenuItem-root': {
                            fontSize: '0.85rem',
                            py: 0.75,
                            px: 2,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              backgroundColor: theme.primary.light + '15',
                              paddingLeft: 2.5
                            },
                            '&.Mui-selected': {
                              backgroundColor: theme.primary.light + '25',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: theme.primary.light + '35'
                              }
                            }
                          }
                        }
                      },
                      transitionDuration: 200,
                      TransitionProps: {
                        timeout: 200
                      }
                    }}
                  >
                    <MenuItem value=""><em>Todos</em></MenuItem>
                    {meses.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6} sm={3} md={1.5}>
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: theme.background.card,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '1px solid transparent',
                      '&:hover': {
                        backgroundColor: 'white',
                        borderColor: theme.primary.light,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.06)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'white',
                        borderColor: theme.primary.main,
                        boxShadow: `0 0 0 2px ${theme.primary.light}20`
                      }
                    }
                  }}
                >
                  <InputLabel sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Año</InputLabel>
                  <Select
                    value={anioSeleccionado}
                    onChange={(e) => setAnioSeleccionado(e.target.value)}
                    label="Año"
                    sx={{ fontSize: '0.85rem' }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          mt: 0.5,
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          maxHeight: 240,
                          '& .MuiMenuItem-root': {
                            fontSize: '0.85rem',
                            py: 0.75,
                            px: 2,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              backgroundColor: theme.primary.light + '15',
                              paddingLeft: 2.5
                            },
                            '&.Mui-selected': {
                              backgroundColor: theme.primary.light + '25',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: theme.primary.light + '35'
                              }
                            }
                          }
                        }
                      },
                      transitionDuration: 200,
                      TransitionProps: {
                        timeout: 200
                      }
                    }}
                  >
                    <MenuItem value=""><em>Todos</em></MenuItem>
                    {generarAnios().map((a) => (
                      <MenuItem key={a} value={a}>{a}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {hayFiltroActivo && (
                <Tooltip title="Limpiar filtros de búsqueda" placement="top">
  <span>
    <Button
      onClick={limpiarFiltros}
      variant="outlined"
      aria-label="Limpiar filtros de búsqueda"
      sx={{
        color: theme.error.main,
        borderColor: theme.error.main,
        minWidth: 'auto',
        p: 1,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: theme.error.main,
          color: 'white',
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 12px ${theme.error.main}40`
        }
      }}
    >
      <FilterAltOffIcon fontSize="small" />
    </Button>
  </span>
</Tooltip>
              )}

<Tooltip title="Exportar historial como PDF" placement="top">
  <span> {/* span necesario porque Tooltip no envuelve directamente elementos disabled */}
    <Button
      onClick={exportarPDF}
      disabled={!historialData}
      variant="contained"
      aria-label="Exportar historial en PDF"
      sx={{
        backgroundColor: theme.error.main,
        color: 'white',
        minWidth: 'auto',
        px: 2.5,
        py: 0.75,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: theme.error.dark,
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        },
        '&:disabled': {
          opacity: 0.5
        }
      }}
    >
      <PictureAsPdfIcon fontSize="small" />
    </Button>
  </span>
</Tooltip>

     <Tooltip title="Exportar historial como Excel (con gráficos)" placement="top">
  <span>
    <Button
      onClick={exportarExcelConGraficos}
      disabled={!historialData}
      variant="contained"
      aria-label="Exportar historial como Excel con gráficos"
      sx={{
        backgroundColor: theme.success.main,
        color: 'white',
        minWidth: 'auto',
        px: 2.5,
        py: 0.75,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: theme.success.dark,
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        },
        '&:disabled': {
          opacity: 0.5
        }
      }}
    >
      <TableViewIcon fontSize="small" />
    </Button>
  </span>
</Tooltip>
            </Box>
          </CardContent>
        </Paper>

        {historialData ? (
          <>
            {/* Banner de Información */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: theme.secondary.light,
                border: `1px solid ${theme.secondary.main}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: theme.text.primary }}>
                {getTituloAnalisis()}
              </Typography>
              {historialData.cobertura && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'white', px: 1.5, py: 0.75, borderRadius: 2, border: `1px solid ${theme.accent.dark}` }}>
                    <LocationOn sx={{ fontSize: 20, color: theme.primary.main }} />
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', display: 'block', color: theme.text.secondary }}>
                        Departamentos
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1, color: theme.text.primary }}>
                        {historialData.cobertura.departamentos}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'white', px: 1.5, py: 0.75, borderRadius: 2, border: `1px solid ${theme.accent.dark}` }}>
                    <LocationOn sx={{ fontSize: 20, color: theme.primary.main }} />
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', display: 'block', color: theme.text.secondary }}>
                        Municipios
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1, color: theme.text.primary }}>
                        {historialData.cobertura.municipios}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>

            {/* Grid de 2 columnas: Gráficos + Tarjetas */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Columna izquierda: Gráficos */}
              <Grid item xs={12} md={9}>
                <Paper
              elevation={1}
              sx={{
                mb: 0.5,
                borderRadius: 1,
                overflow: 'hidden',
                border: `1px solid ${theme.secondary.light}`
              }}
            >
              <Tabs
                value={tabActiva}
                onChange={(e, newValue) => setTabActiva(newValue)}
                variant="fullWidth"
                sx={{
                  backgroundColor: theme.secondary.light,
                  minHeight: 32,
                  '& .MuiTab-root': {
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    py: 0.5,
                    minHeight: 32,
                    textTransform: 'none',
                    color: theme.text.secondary
                  },
                  '& .Mui-selected': {
                    color: theme.primary.main,
                    backgroundColor: 'white'
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: theme.primary.main,
                    height: 2
                  }
                }}
              >
                <Tab label="Gráficos" icon={<BarChart sx={{ fontSize: 16 }} />} iconPosition="start" />
                <Tab label="Línea Tiempo" icon={<CalendarToday sx={{ fontSize: 16 }} />} iconPosition="start" />
                {anioSeleccionado !== '' && (
                  <Tab label="Análisis Ingresos" icon={<TrendingUp sx={{ fontSize: 16 }} />} iconPosition="start" />
                )}
              </Tabs>

              {tabActiva === 0 && (
                <Box sx={{ p: 2, backgroundColor: 'white' }}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: theme.background.card,
                          border: `1px solid ${theme.secondary.light}`,
                          height: '100%'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 0.5 }}>
                         
                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select
                              value={graficoSeleccionado}
                              onChange={(e) => setGraficoSeleccionado(e.target.value)}
                              sx={{ fontSize: '0.7rem', fontWeight: 600, height: 28 }}
                            >
                              <MenuItem value="departamento">Distribución por Departamento</MenuItem>
                              <MenuItem value="municipioNuevos">Distribución por Municipio</MenuItem>
                              
                              <MenuItem value="estado">Distribución por Estado</MenuItem>
                            
                              <MenuItem value="evolucion">Evolución Temporal</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {graficoSeleccionado === 'estado' && (
                          <Box ref={graficoPieRef}>
                            <ResponsiveContainer width="100%" height={350}>
                              <RechartsPie>
                              <Pie
                                data={historialData.graficos.porEstado}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={(e) => `${e.name}: ${e.value}`}
                                outerRadius={120}
                                dataKey="value"
                              >
                                {historialData.graficos.porEstado.map((entry, i) => (
                                  <Cell key={`cell-${i}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <RechartsTooltip content={<CustomTooltip />} />
                              <Legend wrapperStyle={{ fontSize: '0.9rem' }} />
                            </RechartsPie>
                          </ResponsiveContainer>
                          </Box>
                        )}

                        {graficoSeleccionado === 'tipo' && (
                          <ResponsiveContainer width="100%" height={350}>
                            <RechartsBar
                              data={historialData.graficos.porTipo}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke={theme.primary.light} />
                              <XAxis
                                dataKey="name"
                                tick={{ fill: theme.text.primary, fontSize: 10 }}
                              />
                              <YAxis tick={{ fill: theme.text.primary, fontSize: 11 }} />
                              <RechartsTooltip content={<CustomTooltip />} />
                              <Legend wrapperStyle={{ fontSize: '0.9rem' }} />
                              <Bar dataKey="value" name="Cantidad">
                                {historialData.graficos.porTipo.map((entry, i) => (
                                  <Cell key={`cell-${i}`} fill={entry.color} />
                                ))}
                              </Bar>
                            </RechartsBar>
                          </ResponsiveContainer>
                        )}

                        {graficoSeleccionado === 'departamento' && (
                          <Box ref={graficoDeptoRef}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                              <Button
                                size="small"
                                onClick={() => setDeptosExpandido(!deptosExpandido)}
                                variant="text"
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.65rem',
                                  px: 1,
                                  py: 0.25,
                                  color: theme.primary.main,
                                  minHeight: 'auto'
                                }}
                              >
                                {deptosExpandido ? 'Ver menos' : `Ver todos (${historialData.graficos.datosDepartamentos.length})`}
                              </Button>
                            </Box>
                            <ResponsiveContainer width="100%" height={deptosExpandido ? 500 : 350}>
                              <RechartsBar
                                data={deptosExpandido ? historialData.graficos.datosDepartamentos : historialData.graficos.datosDepartamentos.slice(0, 5)}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke={theme.primary.light} />
                                <XAxis
                                  dataKey="departamento"
                                  angle={-45}
                                  textAnchor="end"
                                  height={70}
                                  tick={{ fill: theme.text.primary, fontSize: 11 }}
                                />
                                <YAxis tick={{ fill: theme.text.primary, fontSize: 11 }} />
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '0.9rem' }} />
                                <Bar
                                  dataKey="nuevos"
                                  fill={theme.success.main}
                                  name="Nuevos"
                                  onClick={(data) => handleClickBarra(data, 'nuevos', 'departamento')}
                                  cursor="pointer"
                                />
                                <Bar
                                  dataKey="reelectos"
                                  fill={theme.warning.main}
                                  name="Reelectos"
                                  onClick={(data) => handleClickBarra(data, 'reelectos', 'departamento')}
                                  cursor="pointer"
                                />
                              </RechartsBar>
                            </ResponsiveContainer>
                          </Box>
                        )}

                        {graficoSeleccionado === 'municipioNuevos' && (
                          <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                              <Button
                                size="small"
                                onClick={() => setMunicipiosExpandido(!municipiosExpandido)}
                                variant="text"
                                sx={{
                                  textTransform: 'none',
                                  fontSize: '0.65rem',
                                  px: 1,
                                  py: 0.25,
                                  color: theme.primary.main,
                                  minHeight: 'auto'
                                }}
                              >
                                {municipiosExpandido ? 'Ver menos' : `Ver top 10`}
                              </Button>
                            </Box>
                            <ResponsiveContainer width="100%" height={municipiosExpandido ? 500 : 350}>
                              <RechartsBar
                                data={municipiosExpandido ? historialData.graficos.datosMunicipios : historialData.graficos.datosMunicipios.slice(0, 5)}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke={theme.primary.light} />
                                <XAxis
                                  dataKey="municipio"
                                  angle={-45}
                                  textAnchor="end"
                                  height={90}
                                  tick={{ fill: theme.text.primary, fontSize: 8 }}
                                />
                                <YAxis tick={{ fill: theme.text.primary, fontSize: 11 }} />
                                <RechartsTooltip content={<CustomTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '0.9rem' }} />
                                <Bar
                                  dataKey="nuevos"
                                  fill={theme.success.main}
                                  name="Nuevos"
                                  onClick={(data) => handleClickBarra(data, 'nuevos', 'municipio')}
                                  cursor="pointer"
                                />
                                <Bar
                                  dataKey="reelectos"
                                  fill={theme.warning.main}
                                  name="Reelectos"
                                  onClick={(data) => handleClickBarra(data, 'reelectos', 'municipio')}
                                  cursor="pointer"
                                />
                              </RechartsBar>
                            </ResponsiveContainer>
                          </Box>
                        )}

                        {graficoSeleccionado === 'municipio' && (
                          <Box>
                            {departamentoSeleccionado ? (
                              <>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.75rem', ml: 1 }}>
                                    Municipios de {getNombreDepartamento(departamentoSeleccionado)}
                                  </Typography>
                                  <Button
                                    size="small"
                                    onClick={() => setMunicipiosExpandido(!municipiosExpandido)}
                                    variant="text"
                                    sx={{
                                      textTransform: 'none',
                                      fontSize: '0.65rem',
                                      px: 1,
                                      py: 0.25,
                                      color: theme.primary.main,
                                      minHeight: 'auto'
                                    }}
                                  >
                                    {municipiosExpandido ? 'Ver menos' : `Ver todos (${historialData.graficos.datosMunicipios.length})`}
                                  </Button>
                                </Box>
                                <ResponsiveContainer width="100%" height={municipiosExpandido ? 500 : 350}>
                                  <RechartsBar
                                    data={municipiosExpandido ? historialData.graficos.datosMunicipios : historialData.graficos.datosMunicipios.slice(0, 8)}
                                  >
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.primary.light} />
                                    <XAxis
                                      dataKey="municipio"
                                      angle={-45}
                                      textAnchor="end"
                                      height={90}
                                      tick={{ fill: theme.text.primary, fontSize: 9 }}
                                    />
                                    <YAxis tick={{ fill: theme.text.primary, fontSize: 11 }} />
                                    <RechartsTooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ fontSize: '0.9rem' }} />
                                    <Bar
                                      dataKey="nuevos"
                                      fill={theme.success.main}
                                      name="Nuevos"
                                      onClick={(data) => handleClickBarra(data, 'nuevos', 'municipio')}
                                      cursor="pointer"
                                    />
                                    <Bar
                                      dataKey="reelectos"
                                      fill={theme.warning.main}
                                      name="Reelectos"
                                      onClick={(data) => handleClickBarra(data, 'reelectos', 'municipio')}
                                      cursor="pointer"
                                    />
                                  </RechartsBar>
                                </ResponsiveContainer>
                              </>
                            ) : (
                              <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                                  Selecciona un departamento para ver la distribución por municipios
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}

                        {graficoSeleccionado === 'evolucion' && (
                          <ResponsiveContainer width="100%" height={350}>
                            <LineChart data={historialData.graficos.datosEvolucion}>
                              <CartesianGrid strokeDasharray="3 3" stroke={theme.primary.light} />
                              <XAxis dataKey="anio" tick={{ fill: theme.text.primary, fontSize: 11 }} />
                              <YAxis tick={{ fill: theme.text.primary, fontSize: 11 }} />
                              <RechartsTooltip content={<CustomTooltip />} />
                              <Legend wrapperStyle={{ fontSize: '0.9rem' }} />
                              <Line type="monotone" dataKey="nuevos" stroke={COLORS.nuevo} name="Nuevos" strokeWidth={1.5} />
                              <Line type="monotone" dataKey="reelectos" stroke={COLORS.reelecto} name="Reelectos" strokeWidth={1.5} />
                              <Line type="monotone" dataKey="total" stroke={theme.primary.main} name="Total" strokeWidth={2} />
                            </LineChart>
                          </ResponsiveContainer>
                        )}
                      </Paper>
                </Box>
              )}

              {tabActiva === 1 && (
                <Fade in={true} timeout={600}>
                  <Box sx={{ p: 3 }}>
                    <Paper sx={{ p: 3, mb: 3, backgroundColor: theme.background.card }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CalendarToday sx={{ color: theme.primary.main, fontSize: 28 }} />
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.primary.dark }}>
                          Línea de Tiempo Cronológica
                        </Typography>
                      </Box>
                    
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {departamentoSeleccionado && (
                          <Chip
                            icon={<LocationOn />}
                            label={`${getNombreDepartamento(departamentoSeleccionado)}`}
                            sx={{ backgroundColor: theme.primary.main, color: 'white', fontWeight: 'bold' }}
                          />
                        )}
                        {municipioSeleccionado && (
                          <Chip
                            icon={<LocationOn />}
                            label={`${getNombreMunicipio(municipioSeleccionado)}`}
                            sx={{ backgroundColor: theme.secondary.main, color: 'white', fontWeight: 'bold' }}
                          />
                        )}
                        {asociacionSeleccionada && (
                          <Chip
                            icon={<Business />}
                            label={`${getNombreAsociacion(asociacionSeleccionada)}`}
                            sx={{ backgroundColor: theme.secondary.dark, color: 'white', fontWeight: 'bold' }}
                          />
                        )}
                        {mesSeleccionado !== '' && anioSeleccionado !== '' && (
                          <Chip
                            icon={<CalendarToday />}
                            label={`${meses.find(m => m.value === parseInt(mesSeleccionado))?.label} ${anioSeleccionado}`}
                            sx={{ backgroundColor: theme.success.main, color: 'white', fontWeight: 'bold' }}
                          />
                        )}
                      </Box>
                    </Paper>

                    {historialData.lineaTiempo.length > 0 ? (
                      <Paper sx={{ p: 3, backgroundColor: 'white' }}>
                        <Stepper orientation="vertical" sx={{ mt: 2 }}>
                          {historialData.lineaTiempo.map((item, index) => (
                            <Step key={index} active expanded>
                              <StepLabel
                                StepIconComponent={() => (
                                  <Avatar
                                    sx={{
                                      bgcolor: getStatusColor(item.estatus),
                                      width: 50,
                                      height: 50,
                                      fontWeight: 'bold',
                                      fontSize: '1.2rem',
                                      border: `4px solid ${theme.background.card}`,
                                      boxShadow: 3
                                    }}
                                  >
                                    {item.numeroPeriodo}°
                                  </Avatar>
                                )}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.primary.dark }}>
                                    {item.nombre_representante || 'N/A'}
                                  </Typography>
                                  {item.esReeleccion && (
                                    <Chip
                                      label="REELECCIÓN"
                                      size="small"
                                      sx={{
                                        backgroundColor: theme.secondary.main,
                                        color: 'white',
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem'
                                      }}
                                    />
                                  )}
                                  <Chip
                                    label={item.estatus || 'N/A'}
                                    size="small"
                                    sx={{
                                      backgroundColor: getStatusColor(item.estatus),
                                      color: 'white',
                                      fontWeight: 'bold',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                </Box>
                              </StepLabel>
                              <StepContent>
                                <Paper
                                  sx={{
                                    p: 3,
                                    backgroundColor: theme.background.card,
                                    mb: 2,
                                    border: `2px solid ${getStatusColor(item.estatus)}`,
                                    borderRadius: 2
                                  }}
                                >
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ mb: 2 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: theme.text.secondary, fontWeight: 'bold' }}
                                        >
                                          DNI
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          sx={{ fontWeight: 'bold', color: theme.primary.dark }}
                                        >
                                          {item.dni || 'N/A'}
                                        </Typography>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ mb: 2 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: theme.text.secondary, fontWeight: 'bold' }}
                                        >
                                          Asociación
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          sx={{ fontWeight: 'bold', color: theme.primary.dark }}
                                        >
                                          {getNombreAsociacion(item.cod_asociacion)}
                                        </Typography>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ mb: 2 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: theme.text.secondary, fontWeight: 'bold' }}
                                        >
                                          Departamento
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          sx={{ fontWeight: 'bold', color: theme.primary.dark }}
                                        >
                                          {getNombreDepartamento(item.cod_depto)}
                                        </Typography>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={3}>
                                      <Box sx={{ mb: 2 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: theme.text.secondary, fontWeight: 'bold' }}
                                        >
                                          Municipio
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          sx={{ fontWeight: 'bold', color: theme.primary.dark }}
                                        >
                                          {getNombreMunicipio(item.cod_municipio)}
                                        </Typography>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4}>
                                      <Box sx={{ mb: 2 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: theme.text.secondary, fontWeight: 'bold' }}
                                        >
                                          Fecha de Elección
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          sx={{ fontWeight: 'bold', color: theme.success.main }}
                                        >
                                          {formatDate(item.fecha_eleccion)}
                                        </Typography>
                                      </Box>
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4}>
                                      <Box sx={{ mb: 2 }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ color: theme.text.secondary, fontWeight: 'bold' }}
                                        >
                                          Fecha de Vencimiento
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          sx={{ fontWeight: 'bold', color: theme.error.main }}
                                        >
                                          {formatDate(item.fecha_vencimiento)}
                                        </Typography>
                                      </Box>
                                    </Grid>

                                    {item.telefono && (
                                      <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ mb: 2 }}>
                                          <Typography
                                            variant="caption"
                                            sx={{ color: theme.text.secondary, fontWeight: 'bold' }}
                                          >
                                            Teléfono
                                          </Typography>
                                          <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 'bold', color: theme.primary.dark }}
                                          >
                                            {item.telefono}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    )}

                                    {item.esReeleccion && (
                                      <Grid item xs={12}>
                                        <Box
                                          sx={{
                                            p: 2,
                                            backgroundColor: theme.secondary.light,
                                            borderRadius: 2,
                                            border: `2px solid ${theme.secondary.main}`
                                          }}
                                        >
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TrendingUp sx={{ color: theme.secondary.dark, fontSize: 28 }} />
                                            <Box>
                                              <Typography
                                                variant="body1"
                                                sx={{ fontWeight: 'bold', color: theme.primary.dark }}
                                              >
                                                Periodo {item.numeroPeriodo} de {item.totalPeriodos}
                                              </Typography>
                                              <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                                                {item.numeroPeriodo - 1} reelección(es) acumuladas
                                              </Typography>
                                            </Box>
                                          </Box>
                                        </Box>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Paper>
                              </StepContent>
                            </Step>
                          ))}
                        </Stepper>
                      </Paper>
                    ) : (
                      <Paper sx={{ p: 6, textAlign: 'center', backgroundColor: 'white' }}>
                        <Box
                          sx={{
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            backgroundColor: theme.background.card,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                            mb: 2
                          }}
                        >
                          <CalendarToday sx={{ fontSize: 50, color: theme.primary.light }} />
                        </Box>
                        <Typography variant="h5" sx={{ color: theme.text.secondary, fontWeight: 'bold' }}>
                          No se encontraron registros
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.text.secondary, mt: 1 }}>
                          No hay representantes con los filtros aplicados
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </Fade>
              )}

              {tabActiva === 2 && anioSeleccionado !== '' && (
                <Fade in={true} timeout={600}>
                  <Box sx={{ p: 2, backgroundColor: 'white', overflow: 'visible' }}>
                    <Paper sx={{ p: 2, mb: 2, backgroundColor: theme.background.card }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <TrendingUp sx={{ color: theme.secondary.main, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.primary.dark, fontSize: '1rem' }}>
                          Representantes Electos en el Año {anioSeleccionado}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: theme.text.secondary, mb: 1, fontSize: '0.8rem' }}>
                        {asociacionSeleccionada
                          ? `Asociación: ${getNombreAsociacion(asociacionSeleccionada)} | `
                          : 'Todas las asociaciones | '}
                        Análisis basado en fecha de elección
                      </Typography>

                      {/* Resumen general */}
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={4}>
                          <Paper
                            elevation={2}
                            sx={{
                              p: 1.5,
                              backgroundColor: theme.success.main,
                              color: 'white',
                              textAlign: 'center',
                              borderRadius: 1
                            }}
                          >
                            <Person sx={{ fontSize: 32, mb: 0.5 }} />
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                              {historialData.lineaTiempo.filter(r => !r.esReeleccion).length}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              Acreditados Nuevos 
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5 }}>
                              {((historialData.lineaTiempo.filter(r => !r.esReeleccion).length / historialData.lineaTiempo.length) * 100).toFixed(1)}%
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Paper
                            elevation={2}
                            sx={{
                              p: 1.5,
                              backgroundColor: theme.secondary.main,
                              color: 'white',
                              textAlign: 'center',
                              borderRadius: 1
                            }}
                          >
                            <TrendingUp sx={{ fontSize: 32, mb: 0.5 }} />
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                              {historialData.lineaTiempo.filter(r => r.esReeleccion).length}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                             Acreditados Reelectos
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5 }}>
                              {((historialData.lineaTiempo.filter(r => r.esReeleccion).length / historialData.lineaTiempo.length) * 100).toFixed(1)}%
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Paper
                            elevation={2}
                            sx={{
                              p: 1.5,
                              backgroundColor: theme.primary.main,
                              color: 'white',
                              textAlign: 'center',
                              borderRadius: 1
                            }}
                          >
                            <CheckCircle sx={{ fontSize: 32, mb: 0.5 }} />
                            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                              {historialData.lineaTiempo.length}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                              Total de Acreditaciones en {anioSeleccionado}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5 }}>
                              100%
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* Detalle por departamento y municipio */}
                    {(() => {
                      const ingresosPorDpto = {};
                      historialData.lineaTiempo.forEach(rep => {
                        const depto = getNombreDepartamento(rep.cod_depto);
                        const muni = getNombreMunicipio(rep.cod_municipio);

                        if (!ingresosPorDpto[depto]) {
                          ingresosPorDpto[depto] = {
                            nuevos: [],
                            reelectos: [],
                            municipios: {}
                          };
                        }

                        if (!ingresosPorDpto[depto].municipios[muni]) {
                          ingresosPorDpto[depto].municipios[muni] = {
                            nuevos: [],
                            reelectos: []
                          };
                        }

                        if (rep.esReeleccion) {
                          ingresosPorDpto[depto].reelectos.push(rep);
                          ingresosPorDpto[depto].municipios[muni].reelectos.push(rep);
                        } else {
                          ingresosPorDpto[depto].nuevos.push(rep);
                          ingresosPorDpto[depto].municipios[muni].nuevos.push(rep);
                        }
                      });

                      return Object.entries(ingresosPorDpto)
                        .sort((a, b) => a[0].localeCompare(b[0]))
                        .map(([depto, data]) => (
                          <Paper key={depto} elevation={1} sx={{ p: 2, mb: 1, backgroundColor: 'white', overflow: 'visible' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.primary.dark, fontSize: '0.9rem' }}>
                                {depto}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip
                                  label={`Nuevos: ${data.nuevos.length}`}
                                  size="small"
                                  sx={{ backgroundColor: theme.success.light, color: theme.success.dark, fontWeight: 'bold', fontSize: '0.7rem' }}
                                />
                                <Chip
                                  label={`Reelectos: ${data.reelectos.length}`}
                                  size="small"
                                  sx={{ backgroundColor: theme.secondary.light, color: theme.secondary.dark, fontWeight: 'bold', fontSize: '0.7rem' }}
                                />
                              </Box>
                            </Box>

                            {/* Tabla de representantes */}
                            <Box
                              sx={{
                                display: 'block',
                                width: '100%',
                                overflowX: 'scroll',
                                overflowY: 'visible',
                                WebkitOverflowScrolling: 'touch',
                                '&::-webkit-scrollbar': {
                                  height: '8px',
                                },
                                '&::-webkit-scrollbar-track': {
                                  backgroundColor: '#f1f1f1',
                                  borderRadius: '4px'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                  backgroundColor: theme.primary.main,
                                  borderRadius: '4px',
                                  '&:hover': {
                                    backgroundColor: theme.primary.dark
                                  }
                                }
                              }}
                            >
                                <table style={{ width: '1000px', borderCollapse: 'collapse', fontSize: '0.75rem', tableLayout: 'fixed' }}>
                                <thead>
                                  <tr style={{ backgroundColor: theme.primary.light }}>
                                    <th style={{ padding: '8px', textAlign: 'left', color: 'white', fontWeight: 'bold' }}>Municipio</th>
                                    <th style={{ padding: '8px', textAlign: 'left', color: 'white', fontWeight: 'bold' }}>Nombre Completo</th>
                                    <th style={{ padding: '8px', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>DNI</th>
                                    <th style={{ padding: '8px', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>Tipo</th>
                                    <th style={{ padding: '8px', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>F. Elección</th>
                                    <th style={{ padding: '8px', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>Período</th>
                                    <th style={{ padding: '8px', textAlign: 'center', color: 'white', fontWeight: 'bold' }}>Firma</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(data.municipios)
                                    .sort((a, b) => a[0].localeCompare(b[0]))
                                    .flatMap(([muni, munData]) => {
                                      // Combinar nuevos y reelectos en un solo array
                                      const todos = [
                                        ...munData.nuevos.map(r => ({ ...r, tipo: 'Nuevo' })),
                                        ...munData.reelectos.map(r => ({ ...r, tipo: 'Reelecto' }))
                                      ].sort((a, b) => {
                                        // Ordenar por fecha de elección
                                        const fechaA = a.fecha_eleccion_date || new Date(0);
                                        const fechaB = b.fecha_eleccion_date || new Date(0);
                                        return fechaB - fechaA;
                                      });

                                      return todos.map((rep, idx) => (
                                        <tr
                                          key={`${rep.dni}_${rep.fecha_eleccion}`}
                                          style={{
                                            backgroundColor: idx % 2 === 0 ? theme.background.card : 'white',
                                            borderBottom: '1px solid #e0e0e0'
                                          }}
                                        >
                                          <td style={{ padding: '8px', fontWeight: idx === 0 ? 'bold' : 'normal' }}>
                                            {idx === 0 ? muni : ''}
                                          </td>
                                          <td style={{ padding: '8px' }}>{rep.nombre_representante || 'N/A'}</td>
                                          <td style={{ padding: '8px', textAlign: 'center', fontSize: '0.7rem' }}>
                                            {rep.dni || 'N/A'}
                                          </td>
                                          <td style={{ padding: '8px', textAlign: 'center' }}>
                                            <Chip
                                              label={rep.tipo}
                                              size="small"
                                              sx={{
                                                backgroundColor: rep.tipo === 'Nuevo' ? theme.success.main : theme.secondary.main,
                                                color: 'white',
                                                fontWeight: 'bold',
                                                fontSize: '0.65rem',
                                                height: 22
                                              }}
                                            />
                                          </td>
                                          <td style={{ padding: '8px', textAlign: 'center', fontSize: '0.7rem' }}>
                                            {rep.fecha_eleccion ? new Date(rep.fecha_eleccion).toLocaleDateString('es-HN', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric'
                                            }) : 'N/A'}
                                          </td>
                                          <td style={{ padding: '8px', textAlign: 'center', fontSize: '0.7rem', fontWeight: 'bold' }}>
                                            {rep.numeroPeriodo}° de {rep.totalPeriodos}
                                          </td>
                                          <td style={{ padding: '8px', textAlign: 'center', borderLeft: '1px solid #e0e0e0' }}>
                                            {/* Columna vacía para firma */}
                                          </td>
                                        </tr>
                                      ));
                                    })}
                                </tbody>
                              </table>
                            </Box>
                          </Paper>
                        ));
                    })()}
                  </Box>
                </Fade>
              )}
            </Paper>
              </Grid>

              {/* Columna derecha: Tarjetas de estadísticas */}
              <Grid item xs={12} md={3}>
                <Stack spacing={2}>
                  {[
                    {
                      titulo: 'Acreditados Nuevos',
                      valor: historialData.estadisticas.nuevos,
                      color: theme.success.main,
                      icon: CheckCircle
                    },
                    {
                      titulo: 'Acreditados Reelectos',
                      valor: historialData.estadisticas.reelectos,
                      color: theme.warning.main,
                      icon: TrendingUp
                    }
                  ].map((stat, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        backgroundColor: `${stat.color}15`,
                        borderLeft: `4px solid ${stat.color}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateX(4px)',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                          backgroundColor: `${stat.color}20`
                        }
                      }}
                    >
                      <Box
                        sx={{
                          p: 0.8,
                          borderRadius: 1.5,
                          backgroundColor: stat.color,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <stat.icon sx={{ fontSize: 24 }} />
                      </Box>

                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            color: 'text.secondary',
                            display: 'block',
                            lineHeight: 1.2
                          }}
                        >
                          {stat.titulo}
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 800,
                            color: stat.color,
                            fontSize: '1.5rem',
                            lineHeight: 1
                          }}
                        >
                          {stat.valor}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}

                  {/* Resumen de Estados */}
                  <Paper
                    elevation={1}
                    sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ mb: 1, fontWeight: 700, color: theme.primary.dark, display: 'block', fontSize: '0.75rem' }}
                    >
                      Resumen Estados
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          p: 0.75,
                          backgroundColor: theme.success.light,
                          borderRadius: 1,
                          border: `1px solid ${theme.success.main}`
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 20, color: theme.success.main }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 'bold', color: theme.success.main, lineHeight: 1, fontSize: '1.1rem' }}
                          >
                            {historialData.estadisticas.vigentes}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: theme.text.secondary, fontWeight: 600, fontSize: '0.65rem' }}
                          >
                            Vigentes
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          p: 0.75,
                          backgroundColor: theme.warning.light,
                          borderRadius: 1,
                          border: `1px solid ${theme.warning.main}`
                        }}
                      >
                        <HourglassEmpty sx={{ fontSize: 20, color: theme.warning.main }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 'bold', color: theme.warning.dark, lineHeight: 1, fontSize: '1.1rem' }}
                          >
                            {historialData.estadisticas.vencidos}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: theme.text.secondary, fontWeight: 600, fontSize: '0.65rem' }}
                          >
                            Vencidos
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          p: 0.75,
                          backgroundColor: theme.error.light,
                          borderRadius: 1,
                          border: `1px solid ${theme.error.main}`
                        }}
                      >
                        <Cancel sx={{ fontSize: 20, color: theme.error.main }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 'bold', color: theme.error.main, lineHeight: 1, fontSize: '1.1rem' }}
                          >
                            {historialData.estadisticas.inhabilitados}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: theme.text.secondary, fontWeight: 600, fontSize: '0.65rem' }}
                          >
                            Inhabilitados
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          </>
        ) : (
          <Paper
            elevation={1}
            sx={{
              p: 2,
              textAlign: 'center',
              backgroundColor: 'white',
              borderRadius: 1,
              border: `1px dashed ${theme.secondary.light}`
            }}
          >
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                backgroundColor: theme.secondary.light,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 1
              }}
            >
              <LocationOn sx={{ fontSize: 30, color: theme.primary.main }} />
            </Box>
            <Typography
              variant="body2"
              sx={{ color: theme.primary.dark, mb: 0.5, fontWeight: 700, fontSize: '0.85rem' }}
            >
              Seleccione filtros para ver reporte
            </Typography>
            <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem' }}>
              Use los filtros arriba para visualizar datos
            </Typography>
          </Paper>
        )}

        {/* Modal para mostrar lista de representantes */}
        <Dialog
          open={modalAbierto}
          onClose={() => setModalAbierto(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
            }
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: theme.primary.main,
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
              {tituloModal}
            </Typography>
            <IconButton
              onClick={() => setModalAbierto(false)}
              sx={{
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ mt: 2, p: 3 }}>
            {representantesModal.length === 0 ? (
              <Typography sx={{ color: theme.text.secondary, textAlign: 'center', py: 3 }}>
                No hay representantes para mostrar
              </Typography>
            ) : (
              <Box>
                <Typography variant="body2" sx={{ mb: 2, color: theme.text.secondary, fontSize: '0.85rem' }}>
                  Total: {representantesModal.length} representante(s)
                </Typography>
                <Grid container spacing={2}>
                  {representantesModal.map((rep, index) => (
                    <Grid item xs={12} key={index}>
                      <Card
                        sx={{
                          borderRadius: 1.5,
                          border: `1px solid ${theme.primary.light}`,
                          backgroundColor: 'rgba(139, 111, 71, 0.02)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(139, 111, 71, 0.1)',
                            borderColor: theme.primary.main
                          }
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item>
                              <Avatar
                                sx={{
                                  bgcolor: rep.esReeleccion ? theme.warning.main : theme.success.main,
                                  width: 45,
                                  height: 45
                                }}
                              >
                                <Person />
                              </Avatar>
                            </Grid>
                            <Grid item xs>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.primary.dark,
                                  mb: 0.5,
                                  fontSize: '0.95rem'
                                }}
                              >
                                {rep.nombre_representante || 'N/A'}
                              </Typography>
                              <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <Grid item>
                                  <Chip
                                    label={rep.esReeleccion ? 'Reelecto' : 'Nuevo'}
                                    size="small"
                                    sx={{
                                      backgroundColor: rep.esReeleccion ? theme.warning.light : theme.success.light,
                                      color: rep.esReeleccion ? theme.warning.dark : theme.success.dark,
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      height: 20
                                    }}
                                  />
                                </Grid>
                                <Grid item>
                                  <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.75rem' }}>
                                    DNI: {rep.dni}
                                  </Typography>
                                </Grid>
                                <Grid item>
                                  <Chip
                                    label={`${rep.periodos.length} periodo${rep.periodos.length > 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: theme.secondary.light,
                                      color: theme.secondary.dark,
                                      fontWeight: 600,
                                      fontSize: '0.7rem',
                                      height: 20
                                    }}
                                  />
                                </Grid>
                              </Grid>
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.75rem', mb: 0.5, display: 'block' }}>
                                  <LocationOn sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                                  {getNombreMunicipio(rep.cod_municipio)} - {getNombreDepartamento(rep.cod_depto)}
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: theme.primary.dark, fontSize: '0.75rem', display: 'block', mb: 0.5, mt: 1 }}>
                                  Historial de Periodos (Todas las Asociaciones):
                                </Typography>
                                {rep.periodos.map((periodo, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      ml: 2,
                                      mb: 0.75,
                                      p: 0.75,
                                      backgroundColor: 'rgba(139, 111, 71, 0.05)',
                                      borderRadius: 1,
                                      borderLeft: `3px solid ${theme.primary.main}`
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                      <CalendarToday sx={{ fontSize: 10, verticalAlign: 'middle', mr: 0.5 }} />
                                      Periodo {idx + 1}: {periodo.fecha_eleccion ? new Date(periodo.fecha_eleccion).toLocaleDateString('es-CO') : 'N/A'}
                                      {periodo.fecha_vencimiento && ` - ${new Date(periodo.fecha_vencimiento).toLocaleDateString('es-CO')}`}
                                      {' '}
                                      <Chip
                                        label={periodo.estatus === 'VIGENTE' ? 'Vigente' : periodo.estatus === 'VENCIDA' ? 'Vencido' : 'Inhabilitado'}
                                        size="small"
                                        sx={{
                                          ml: 0.5,
                                          height: 16,
                                          fontSize: '0.65rem',
                                          backgroundColor:
                                            periodo.estatus === 'VIGENTE' ? theme.success.light :
                                            periodo.estatus === 'VENCIDA' ? theme.error.light :
                                            theme.warning.light,
                                          color:
                                            periodo.estatus === 'VIGENTE' ? theme.success.dark :
                                            periodo.estatus === 'VENCIDA' ? theme.error.dark :
                                            theme.warning.dark
                                        }}
                                      />
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: theme.text.secondary, fontSize: '0.7rem', display: 'block' }}>
                                      <Business sx={{ fontSize: 10, verticalAlign: 'middle', mr: 0.5 }} />
                                      Asociación: {getNombreAsociacion(periodo.cod_asociacion)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.primary.light}` }}>
          
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default HistorialDepartamento;